import { Packr, Unpackr } from "msgpackr";
import pako from "pako";
import { isUuid } from "../MiscUtils";
import { normalizeText } from "../NormalizeText";
import { type Section, type SectionEntry, type UnsignedColumn, type SignedColumn, columnType, narrowSection, integerSection, signedSection, readSections, sectionEntry } from "./DecisionGraphFormat";
import { type DecisionGraphRelease, decisionGraphRelease, documentRef } from "./DecisionGraphRelease";

/**
 * A solved decision graph as produced by the engine's `{type: "decisionGraph"}` response element: one binary
 * container (see DECISION_GRAPH_FORMAT.md at the repository root for the layout).
 *
 * Nodes are integer ids. A node is a release template (rule graph node) plus an optional entity instance, so paths,
 * indexes and parents are derived on demand instead of being stored per node, and edges are integer adjacency. The
 * integer columns are typed arrays over the container (no copies); a node's values are only unpacked when read.
 * Rule detail (description, conditions, ...) lives on the release: look it up with `guid(id)`.
 */

export type DecisionGraphReason = { type: string; data?: unknown };

export type DecisionGraphNodeValues = {
  input?: unknown;
  derived?: unknown;
  justification?: string;
  triggered?: unknown[];
  /** Why the node is unknown/uncertain. Only present when the graph was requested with `debug`. */
  reason?: DecisionGraphReason;
};

/** Bytes from the engine, a saved/response form (deflated, bytes or base64), or a graph. */
export type DecisionGraphInput = Uint8Array | ArrayBuffer | StoredDecisionGraph | DecisionGraph;

export const DECISION_GRAPH_MAGIC = "DCGR";
/**
 * The v1 codec, shared with the engine. Keep it available when adding a new codec: do not just bump this constant.
 * Adding optional sections/header fields is compatible; changing existing layouts needs a separate versioned codec.
 */
export const DECISION_GRAPH_FORMAT_VERSION = 1;
/** Wire version written to storage and HTTP responses. The current draft has not been released. */
export const DECISION_GRAPH_COMPATIBILITY_VERSION = 1;

export class UnsupportedDecisionGraphFormatError extends Error {
  constructor(version: number | undefined, supportedVersion: number) {
    super(
      version === undefined
        ? "Unsupported decision graph format (written before formatVersion)"
        : `Unsupported decision graph format ${version} (this reader reads ${supportedVersion})`,
    );
    this.name = "UnsupportedDecisionGraphFormatError";
  }
}

/** New layouts get separate keys; existing readers continue using the v1 `.dg` sibling. */
export const decisionGraphStorageKey = (base: string, formatVersion = DECISION_GRAPH_COMPATIBILITY_VERSION): string => {
  if (!Number.isSafeInteger(formatVersion) || formatVersion < 1)
    throw new Error("Invalid decision graph format version");
  return formatVersion === 1 ? `${base}.dg` : `${base}.v${formatVersion}.dg`;
};
/** Edge source flag: a rule graph edge between global nodes. */
export const EDGE_FROM_RELEASE = 1;
/** Edge source flag: an edge the solve resolved (child keys, or a childless sub-entity's hidden node). */
export const EDGE_FROM_STATE = 2;
/**
 * Edge source flag: a child key of a node the solve didn't evaluate. Solved views and the legacy graph leave these out
 * (they read EDGE_FROM_STATE); a decision report walks them, as the engine's report does.
 */
export const EDGE_FROM_STRUCTURE = 4;

// presence flags leading a node's value record
const VALUE_INPUT = 1;
const VALUE_DERIVED = 2;
const VALUE_TRIGGERED = 4;

type Header = {
  /** The layout version (DECISION_GRAPH_FORMAT_VERSION); absent in files written before it existed. */
  formatVersion?: number;
  /** Defaults to 1. New formats must retain the v1 sections unless they explicitly require a newer reader. */
  minReaderVersion?: number;
  id?: string;
  session: boolean;
  debug: boolean;
  options?: Record<string, unknown>;
  nodeCount: number;
  templates: string[];
  templateEntity: Uint8Array;
  entityNames: string[];
  externalIds: string[];
  justifications: string[];
  sections: Section[];
  /** A release or client rule graph's own label (its `value`), when it has one (toRuleGraph). */
  ruleGraphValue?: unknown;
  /** A session's entity structure (persistent state the engine reads back), when the graph carries its state. */
  entityStructure?: unknown;
};

const packr = new Packr({ useRecords: false });
// 64-bit integers (the engine's, and large rule data integers) read as numbers
const unpackr = new Unpackr({ useRecords: false, mapsAsObjects: true, int64AsType: "number" });
const MAGIC = new TextEncoder().encode(DECISION_GRAPH_MAGIC);

// Node's Buffer when there is one (browsers and workers have neither it nor its types)
const NodeBuffer: any = (globalThis as any).Buffer;

export const bytesToBase64 = (bytes: Uint8Array): string => {
  if (NodeBuffer) return NodeBuffer.from(bytes.buffer, bytes.byteOffset, bytes.byteLength).toString("base64");
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
};

export const base64ToBytes = (value: string): Uint8Array => {
  if (NodeBuffer) return new Uint8Array(NodeBuffer.from(value, "base64"));
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

/**
 * How a decision graph is saved (session graphs, decision reports): the deflated container. Storage keeps the
 * bytes raw; JSON responses carry them as base64 (see toResponseDecisionGraph).
 */
export type StoredDecisionGraph = { decisionGraph: Uint8Array | string; encoding?: "deflate"; sourceModifiedAt?: string };

export const isStoredDecisionGraph = (value: unknown): value is StoredDecisionGraph =>
  typeof value === "object" &&
  value !== null &&
  (typeof (value as any).decisionGraph === "string" || (value as any).decisionGraph instanceof Uint8Array);

/** The bytes to save for a decision graph: the deflated container. */
export const encodeStoredDecisionGraph = (bytes: Uint8Array): Uint8Array => pako.deflate(bytes);

/** A saved decision graph's bytes (from encodeStoredDecisionGraph), read back from storage. */
export const fromStoredDecisionGraphBytes = (bytes: Uint8Array): StoredDecisionGraph => ({
  decisionGraph: bytes,
  encoding: "deflate",
});

export const toStoredDecisionGraph = (bytes: Uint8Array): StoredDecisionGraph =>
  fromStoredDecisionGraphBytes(encodeStoredDecisionGraph(bytes));

/** A saved decision graph's container bytes (inflated). */
export const storedDecisionGraphBytes = (stored: StoredDecisionGraph): Uint8Array => {
  const bytes = typeof stored.decisionGraph === "string" ? base64ToBytes(stored.decisionGraph) : stored.decisionGraph;
  return stored.encoding === "deflate" ? pako.inflate(bytes) : bytes;
};

/** A decision graph as it goes in a JSON response: the saved form, with its bytes as base64. */
export const toResponseDecisionGraph = (
  value: Uint8Array | StoredDecisionGraph | (Uint8Array | StoredDecisionGraph)[],
): StoredDecisionGraph | StoredDecisionGraph[] => {
  if (Array.isArray(value)) return value.map((item) => toResponseDecisionGraph(item) as StoredDecisionGraph);
  const stored = isStoredDecisionGraph(value) ? value : toStoredDecisionGraph(value as Uint8Array);
  // A future encoder must retain a v1 projection for existing callers, not silently change their response format.
  assertDecisionGraphVersion(storedDecisionGraphBytes(stored), DECISION_GRAPH_COMPATIBILITY_VERSION);
  const { sourceModifiedAt: _storageRevision, ...wire } = stored;
  return typeof stored.decisionGraph === "string" ? wire : { ...wire, decisionGraph: bytesToBase64(stored.decisionGraph) };
};

export const isDecisionGraphBytes = (value: unknown): value is Uint8Array =>
  value instanceof Uint8Array && value.length >= 8 && MAGIC.every((byte, i) => value[i] === byte);

/** Check the header before reading sections or publishing bytes under a version's storage/HTTP contract. */
export const assertDecisionGraphVersion = (bytes: Uint8Array, version = DECISION_GRAPH_FORMAT_VERSION): void => {
  if (!isDecisionGraphBytes(bytes)) throw new Error("Not a decision graph");
  const length = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(4, true);
  if (length > bytes.length - 8) throw new Error("Truncated decision graph header");
  const header = unpackr.unpack(bytes.subarray(8, 8 + length)) as Header;
  if (header.formatVersion !== version) {
    throw new UnsupportedDecisionGraphFormatError(header.formatVersion, version);
  }
};

const toBytes = (input: Uint8Array | ArrayBuffer | StoredDecisionGraph): Uint8Array => {
  if (isStoredDecisionGraph(input)) return storedDecisionGraphBytes(input);
  return input instanceof Uint8Array ? input : new Uint8Array(input);
};

/** A validation as the result's `validations` lists it (DecisionGraph.validations). */
export type DecisionGraphValidation = {
  message: string;
  severity: string;
  shown: boolean;
  /** The instance's path; null for a global validation. */
  parent: string | null;
  /** The validation rule's id. */
  id: string;
  /** The paths of the attributes it depends on. */
  attributes: string[];
};

/** How node objects (node(id)) look. */
export type DecisionGraphOptions = {
  /**
   * The release, for rule data on node objects (the full debug graph the portal's views show). Without it (or with
   * `minimal`) node objects are decisionapi's implicit report graph: values and debug reasons only.
   */
  release?: DecisionGraphRelease | { rule_graph?: any; documents?: any };
  /**
   * The release's documents, for a graph that carries its own rule data (a release's compiled graph, see
   * releaseGraphToDecisionGraph): node objects show definedIn/usedIn by document name, as with `release`.
   */
  documents?: any;
  minimal?: boolean;
  /** The id in label(), for the `graph` element this graph stands in for. */
  id?: string;
};

type NodeObject = Record<string, any>;
const UNSET = Symbol("unset");

/** A new, empty graph's options (`new DecisionGraph(options)`). */
export type DecisionGraphGraphOptions = { directed?: boolean; multigraph?: boolean; compound?: boolean };

/**
 * A graph's edits, over its container: nodes added (ids from `size` up, in order) and removed, node data read or set
 * (`data`, what node() reads and writes), and edges added, removed or relabelled. The container is never changed;
 * toBytes() writes a new one when there are edits.
 */
type DecisionGraphEdits = {
  removed: Set<number>;
  added: string[];
  addedIds: Map<string, number>;
  /** Edge-only nodes (ids from `listedCount` to `size`) made nodes of their own (createRef, setNode). */
  listed: Set<number>;
  /** Nodes added or listed, in order: ids() lists them after the container's. */
  order: number[];
  data: Map<number, NodeObject>;
  /** Nodes whose data was written (setNode, or a write through node()); others in `data` were only read. */
  changed: Set<number>;
  proxies: Map<number, NodeObject>;
  edgesAdded: Map<number, Map<number, unknown>>;
  edgesAddedIn: Map<number, Set<number>>;
  edgesRemoved: Map<number, Set<number>>;
  edgeLabels: Map<number, Map<number, unknown>>;
  /** Every edge added, in order (from, to pairs; an edge removed and added again appears again): the edges' order. */
  edgeLog: number[];
  label?: { value: unknown };
  /** Count of node, edge and label edits (changes to the graph other than node data). */
  shape: number;
  /** Instances' node objects made over their template's edited data (view), with the revision they were made at. */
  views: Map<number, { revision: number; object: NodeObject | undefined }>;
  /**
   * Per template pair (template indexes, see templateIndex), how many edges between their instances (not both
   * global) there are: each implies an edge between the templates' global nodes, as the container's reader has it.
   * Counted when first needed, then kept up to date.
   */
  implied?: { out: Map<number, Map<number, number>>; in: Map<number, Map<number, number>> };
};

/** A graph as legacy JSON (graphlib's json.write): exports and graphs saved before decision graphs. */
export type GraphJson = {
  options?: DecisionGraphGraphOptions;
  value?: unknown;
  nodes: { v: string; value?: any; parent?: string }[];
  edges?: { v: string; w: string; name?: string; value?: unknown }[];
};

const isGraphJson = (input: unknown): input is GraphJson =>
  typeof input === "object" && input !== null && !(input instanceof Uint8Array) && Array.isArray((input as any).nodes);

/** What an added node's path says about it: `<entity>/<index>/.../<guid>`, or a global guid. */
const addedPath = (path: string) => {
  const parts = path.split("/");
  const guid = parts[parts.length - 1];
  if (parts.length < 3) return { guid, instancePath: undefined, entity: undefined, index: undefined, parentPath: undefined };
  const instance = parts.slice(0, -1);
  return {
    guid,
    instancePath: instance.join("/"),
    entity: instance[instance.length - 2],
    index: instance[instance.length - 1] as string | number,
    parentPath: instance.length > 2 ? instance.slice(0, -2).join("/") : undefined,
  };
};

/** Graphs made from legacy JSON (and graphlib) objects, once per object. */
const fromLegacy = new WeakMap<object, DecisionGraph>();

/**
 * A legacy graph in any of its forms as JSON: the JSON itself, deflated JSON (bytes, a number array, a serialised
 * Buffer or a numbered object), a graphlib graph object, or `{}` (an older export's empty graph).
 */
const legacyGraphJson = (input: any): GraphJson => {
  if (isGraphJson(input)) return input;
  if (typeof input?.nodes === "function") {
    return {
      options: { directed: input.isDirected?.() ?? true, multigraph: input.isMultigraph?.() ?? false, compound: input.isCompound?.() ?? false },
      value: input.graph?.(),
      nodes: input.nodes().map((v: string) => ({ v, value: input.node(v), ...(input.parent?.(v) !== undefined ? { parent: input.parent(v) } : {}) })),
      edges: input.edges().map((edge: any) => ({ ...edge, value: input.edge(edge) })),
    };
  }
  if (typeof input === "object" && input !== null && Object.prototype.toString.call(input) === "[object Object]" && Object.keys(input).length === 0) {
    return { nodes: [], edges: [] };
  }
  const bytes = input instanceof Uint8Array ? input
    : Array.isArray(input) ? new Uint8Array(input)
    : input?.type === "Buffer" ? new Uint8Array(input.data ?? Object.values(input))
    : typeof input === "object" && input !== null && Object.values(input).every((v) => typeof v === "number") ? new Uint8Array(Object.values(input) as number[])
    : undefined;
  if (!bytes) throw new Error("Not a recognised graph");
  try {
    return JSON.parse(pako.inflate(bytes, { to: "string" }));
  } catch {
    throw new Error("Failed to uncompress graph");
  }
};

const emptyContainers = new Map<string, Uint8Array>();

/** The container of a new graph with no nodes (made once per set of options). */
const emptyDecisionGraph = (options: DecisionGraphGraphOptions): Uint8Array => {
  const graphOptions = { directed: options.directed ?? true, multigraph: options.multigraph ?? false, compound: options.compound ?? false };
  const key = JSON.stringify(graphOptions);
  let bytes = emptyContainers.get(key);
  if (!bytes) emptyContainers.set(key, (bytes = graphRecordsToDecisionGraph([], [], graphOptions, undefined, { session: false, ruleData: true })));
  return bytes;
};

/** A copy of a node value, so edits through node() never reach the container's shared decodes. */
const copyValue = (value: any): any => {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(copyValue);
  if (value instanceof Uint8Array || value instanceof Date) return value;
  const copy: Record<string, any> = {};
  for (const key of Object.keys(value)) copy[key] = copyValue(value[key]);
  return copy;
};

// imminently_fn's removeUndefinedFields, as decisionapi applied it to hydrated instances
const removeUndefinedFields = (obj: any) => {
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value && typeof value === "object") removeUndefinedFields(value);
    else if (value === undefined) delete obj[key];
  }
  return obj;
};

/** A description index for an immutable graph or a mutable cleanup pass. */
export type GraphFieldLookup = { field: string; value: string; normalized?: boolean; normalize?: (text: string) => string };

export class DecisionGraphDescriptionIndex<T> {
  private readonly exact = new Map<string | undefined, Set<T>>();
  private readonly normalized = new Map<string, Set<T>>();
  private readonly keys = new Map<T, { description: string | undefined; normalized?: string }>();

  constructor(nodes: Iterable<T>, private readonly describe: (node: T) => string | undefined,
    private readonly identity: (node: T) => unknown,
    private readonly normalize: (text: string) => string = text => normalizeText(text).toLowerCase()) {
    for (const node of nodes) this.add(node);
  }

  add(node: T): void {
    const description = this.describe(node);
    if (this.keys.has(node) && this.keys.get(node)!.description === description) return;
    this.remove(node);
    const key = description !== undefined ? this.normalize(description) : undefined;
    this.keys.set(node, { description, normalized: key });
    let exact = this.exact.get(description);
    if (!exact) this.exact.set(description, exact = new Set());
    exact.add(node);
    if (key !== undefined) {
      let matches = this.normalized.get(key);
      if (!matches) this.normalized.set(key, matches = new Set());
      matches.add(node);
    }
  }

  find(description: string | undefined, normalized = false): T[] {
    if (normalized) return description === undefined ? [] : [...(this.normalized.get(this.normalize(description)) ?? [])];
    return [...(this.exact.get(description) ?? [])];
  }

  hasOther(description: string, excludedIdentity: unknown): boolean {
    const matches = this.normalized.get(this.normalize(description));
    if (matches) for (const node of matches) if (this.identity(node) !== excludedIdentity) return true;
    return false;
  }

  /** Remove a pruned node without rebuilding or scanning the graph. */
  remove(node: T): void {
    const keys = this.keys.get(node);
    if (!keys) return;
    this.exact.get(keys.description)?.delete(node);
    if (keys.normalized !== undefined) this.normalized.get(keys.normalized)?.delete(node);
    this.keys.delete(node);
  }
}

/**
 * The decision graph. Every method takes and returns integer node ids (0..size-1); `ref(path)` turns a path from
 * outside into an id and `deref(id)` turns an id back into a path for anything leaving the graph.
 *
 * Nothing is built up front: the integer columns are views over the container, node values are unpacked when read,
 * node objects (node(id)) and paths are made on first use and kept, adjacency is built once when first walked.
 */
export class DecisionGraph {
  private readonly fieldIndexes = new Map<string, Map<GraphFieldLookup["normalize"], DecisionGraphDescriptionIndex<number>>>();

  /** Build a snapshot index; mutable callers remove pruned nodes from it. */
  static createDescriptionIndex<T>(nodes: Iterable<T>, describe: (node: T) => string | undefined, identity: (node: T) => unknown = node => node, normalize?: (text: string) => string) {
    return new DecisionGraphDescriptionIndex(nodes, describe, identity, normalize);
  }

  /** Cached exact or normalized description lookup, returning integer node ids. */
  nodesByDescription(description: string, options: Omit<GraphFieldLookup, "field" | "value"> = {}): number[] {
    return this.nodesByField("description", description, options);
  }

  nodesByField(field: string, value: string, options: Omit<GraphFieldLookup, "field" | "value"> = {}): number[] {
    if (typeof value !== "string") return [];
    let byNormalizer = this.fieldIndexes.get(field);
    if (!byNormalizer) this.fieldIndexes.set(field, byNormalizer = new Map());
    let index = byNormalizer.get(options.normalize);
    if (!index) {
      index = DecisionGraph.createDescriptionIndex(this.ids(), id => {
        const value = field === "$key" ? this.deref(id) : this.field(id, field);
        return typeof value === "string" ? value : undefined;
      }, undefined, options.normalize);
      byNormalizer.set(options.normalize, index);
    }
    const found = index.find(value, options.normalized || !!options.normalize);
    // edits add to an index out of order: give the nodes in graph order (ids ascend through it)
    return this.edit ? found.sort((a, b) => a - b) : found;
  }

  nodesByFields(queries: GraphFieldLookup[]): number[] {
    return [...new Set(queries.flatMap(({ field, value, ...options }) => this.nodesByField(field, value, options)))].sort((a, b) => a - b);
  }
  readonly id: string | undefined;
  /**
   * The container's nodes with their own values; the rest (to size) were only reached as edge targets. Container only:
   * iterate ids() and size masks with idBound(), which include edits.
   */
  private readonly listedCount: number;
  /** Every container node id, including edge-only targets (see listedCount). */
  private readonly size: number;
  /** Whether the graph was solved in session mode. */
  readonly session: boolean;
  /** Whether debug reasons were collected. */
  readonly debug: boolean;
  /** The release rule graph's graphlib options. */
  readonly options: Record<string, unknown> | undefined;
  /** Release node guids, indexed by template (engine GraphNodeId). */
  readonly templates: string[];
  /** Node objects are the implicit report graph's (no rule data). */
  readonly minimal: boolean;

  /**
   * Whether the graph carries a session's persistent state (the engine's `state` sections): a session's saved graph,
   * which is passed back to the engine's solve as the session's state. JS doesn't read the state itself.
   */
  get hasState(): boolean {
    return this.header.sections.some((section) => section.name === "nodeState");
  }

  /**
   * Whether the graph carries its templates' rule data (`ruleData`, a release's compiled graph): node objects then
   * have it without being given the release.
   */
  get hasRuleData(): boolean {
    return this.header.sections.some((section) => section.name === "ruleData");
  }

  /** Whether the graph carries validations (its decisionGraph element asked for `validations`). */
  get hasValidations(): boolean {
    return this.header.sections.some((section) => section.name === "validationNodes");
  }

  /**
   * The validations the graph carries, as the result's `validations` lists them: each one's rule id, its instance
   * (`parent`, null for a global one), message, severity, whether it's shown, and its attributes' paths. Undefined
   * when the graph doesn't carry them.
   */
  validations(): DecisionGraphValidation[] | undefined {
    return this.validationRecords()?.map((validation) => ({
      message: validation.message,
      severity: validation.severity,
      shown: validation.shown,
      parent: this.instancePath(validation.node) ?? null,
      id: this.guid(validation.node),
      attributes: validation.attributes.map((id) => this.deref(id)),
    }));
  }

  /** The validations the container carries, by node id (as writeDecisionGraph takes them). */
  private validationRecords(): NonNullable<DecisionGraphParts["validations"]> | undefined {
    const nodes = this.sectionView("validationNodes", Uint32Array);
    if (!nodes) return undefined;
    const shown = this.sectionView("validationShown", Uint8Array)!;
    const text = this.sectionView("validationText", Uint32Array)!;
    const textOffsets = this.sectionView("validationTextOffsets", Uint32Array)!;
    const textData = this.sectionView("validationTextData", Uint8Array)!;
    const counts = this.sectionView("validationAttributeCounts", Uint32Array)!;
    const attributes = this.sectionView("validationAttributes", Int32Array)!;
    const texts = new Map<number, [string, string]>();
    const out: NonNullable<DecisionGraphParts["validations"]> = [];
    for (let v = 0, a = 0; v < nodes.length; v++) {
      const record = text[v] - 1;
      let severityMessage = texts.get(record);
      if (!severityMessage) {
        severityMessage = unpackr.unpack(textData.subarray(textOffsets[record], textOffsets[record + 1])) as [string, string];
        texts.set(record, severityMessage);
      }
      const node = nodes[v];
      const ids: number[] = [];
      for (let previous = node, end = a + counts[v]; a < end; a++) {
        previous += attributes[a];
        ids.push(previous);
      }
      out.push({ node, severity: severityMessage[0], message: severityMessage[1], shown: shown[v] === 1, attributes: ids });
    }
    return out;
  }

  /** The rule data node objects read (the given release's, or what the graph carries); undefined for minimal graphs. */
  get ruleRelease(): DecisionGraphRelease | undefined {
    return this.release;
  }

  /**
   * The rule graph a release's compiled graph was written from (releaseGraphToDecisionGraph), exactly: its nodes in
   * order with their values, its edges in order, its options. Undefined for a graph without rule data.
   */
  toRuleGraph():
    | {
        options?: Record<string, unknown>;
        value?: unknown;
        nodes: { v: string; value?: any }[];
        edges: ({ v: string; w: string } & Record<string, unknown>)[];
      }
    | undefined {
    const offsets = this.sectionView("ruleOffsets", Uint32Array);
    const data = this.sectionView("ruleData", Uint8Array);
    const edges = this.sectionView("ruleEdges", Uint32Array);
    if (!offsets || !data || !edges) return undefined;
    const record = (name: string) => {
      const recordOffsets = this.sectionView(`${name}Offsets`, Uint32Array);
      const recordData = this.sectionView(`${name}Data`, Uint8Array);
      return (i: number) =>
        recordOffsets && recordData && recordOffsets[i] !== recordOffsets[i + 1]
          ? unpackr.unpack(recordData.subarray(recordOffsets[i], recordOffsets[i + 1]))
          : undefined;
    };
    const nodeExtra = record("ruleNodeExtra");
    const edgeLabel = record("ruleEdge");
    const nodes: { v: string; value?: any }[] = [];
    for (let id = 0; id < offsets.length - 1; id++) {
      const node: { v: string; value?: any } = { v: this.deref(id) };
      if (offsets[id] !== offsets[id + 1]) node.value = unpackr.unpack(data.subarray(offsets[id], offsets[id + 1]));
      nodes.push({ ...node, ...nodeExtra(id) });
    }
    const ruleEdges: { v: string; w: string }[] = [];
    for (let i = 0; i + 1 < edges.length; i += 2) {
      ruleEdges.push({ v: this.deref(edges[i]), w: this.deref(edges[i + 1]), ...edgeLabel(i / 2) });
    }
    return {
      ...(this.options !== undefined ? { options: this.options } : {}),
      ...(this.header.ruleGraphValue !== undefined ? { value: this.header.ruleGraphValue } : {}),
      nodes,
      edges: ruleEdges,
    };
  }

  /** Original data revision retained by format-only storage healing. */
  get sourceModifiedAt(): string | undefined { return (this.header as any).sourceModifiedAt; }

  /** Rule records for graph builders; does not export a JSON graph. */
  ruleNodeRecord(id: number): { v: string; value?: any; parent?: string } {
    const offsets = this.sectionView("ruleOffsets", Uint32Array);
    const data = this.sectionView("ruleData", Uint8Array);
    if (!offsets || !data) return { v: this.deref(id), value: this.node(id) };
    const extraOffsets = this.sectionView("ruleNodeExtraOffsets", Uint32Array);
    const extraData = this.sectionView("ruleNodeExtraData", Uint8Array);
    return {
      v: this.deref(id),
      ...(offsets[id] !== offsets[id + 1] ? { value: unpackr.unpack(data.subarray(offsets[id], offsets[id + 1])) } : {}),
      ...(extraOffsets && extraData && extraOffsets[id] !== extraOffsets[id + 1]
        ? unpackr.unpack(extraData.subarray(extraOffsets[id], extraOffsets[id + 1])) : {}),
    };
  }

  *ruleEdgeRecords(): IterableIterator<{ v: string; w: string; name?: string; value?: any }> {
    const edges = this.sectionView("ruleEdges", Uint32Array);
    if (!edges) {
      for (const [from, to] of this.containerEdgeEntries()) yield { v: this.deref(from), w: this.deref(to) };
      return;
    }
    const offsets = this.sectionView("ruleEdgeOffsets", Uint32Array);
    const data = this.sectionView("ruleEdgeData", Uint8Array);
    for (let i = 0; i < edges.length / 2; i++) {
      yield { v: this.deref(edges[i * 2]), w: this.deref(edges[i * 2 + 1]),
        ...(offsets && data && offsets[i] !== offsets[i + 1] ? unpackr.unpack(data.subarray(offsets[i], offsets[i + 1])) : {}) };
    }
  }

  ruleGraphLabel(): unknown { return this.header.ruleGraphValue; }

  /** A section as a typed array over the container; undefined when the graph doesn't have it. */
  private sectionView(name: string, type: Uint8ArrayConstructor): Uint8Array | undefined;
  private sectionView(name: string, type: Uint32ArrayConstructor): UnsignedColumn | undefined;
  private sectionView(name: string, type: Int32ArrayConstructor): SignedColumn | undefined;
  private sectionView(name: string, type: Uint8ArrayConstructor | Uint32ArrayConstructor | Int32ArrayConstructor): Uint8Array | UnsignedColumn | SignedColumn | undefined {
    const section = this.header.sections.find((s) => s.name === name);
    if (!section) return undefined;
    const headerLength = new DataView(this.bytes.buffer, this.bytes.byteOffset, 8).getUint32(4, true);
    const start = this.bytes.byteOffset + Math.ceil((8 + headerLength) / 8) * 8 + section.offset;
    if (type === Uint8Array) return new Uint8Array(this.bytes.buffer, start, section.length);
    const Type = columnType(type === Int32Array, section.width);
    if (section.length % section.width) throw new Error(`Invalid integer section: ${name}`);
    return new Type(this.bytes.buffer as ArrayBuffer, start, section.length / section.width);
  }

  private propertyKeys?: string[];
  private propertyOffsets?: UnsignedColumn;
  private propertyFlags?: Uint8Array;
  private propertyNodes?: UnsignedColumn;
  private propertyRecords?: UnsignedColumn;
  private primitivePropertyRecords = new Map<number, Record<string, unknown>>();
  private propertyData?: Uint8Array;
  private propertyCache = new Map<number, Record<string, unknown>>();

  /** Names explicitly marked to survive subsequent solves; all other extras are transient. */
  get persistentProperties(): string[] {
    return this.propertyKeys?.filter((_, index) => (this.propertyFlags?.[index] ?? 0) & 1) ?? [];
  }

  /**
   * Extra properties owned by this node (without template inheritance). They're the container's: a graph with edits
   * throws, so call toPacked() first (its ids are its own).
   */
  properties(id: number): Record<string, unknown> | undefined {
    this.assertPacked("properties");
    return this.containerProperties(id);
  }

  private containerProperties(id: number): Record<string, unknown> | undefined {
    if (id < 0 || id >= this.listedCount || !this.propertyOffsets) return undefined;
    let row = id;
    if (this.propertyNodes) {
      let low = 0, high = this.propertyNodes.length;
      while (low < high) {
        const middle = (low + high) >>> 1;
        if (this.propertyNodes[middle] < id) low = middle + 1;
        else high = middle;
      }
      if (this.propertyNodes[low] !== id) return undefined;
      row = low;
    }
    const reference = this.propertyRecords?.[row];
    if (reference === 0) return undefined;
    const record = reference === undefined ? row : reference - 1;
    const start = this.propertyOffsets[record];
    const end = this.propertyOffsets[record + 1];
    if (start === end) return undefined;
    if (!this.propertyCache.has(id)) {
      const primitive = this.primitivePropertyRecords.get(record);
      if (primitive) {
        const own = { ...primitive };
        this.propertyCache.set(id, own);
        return own;
      }
      const pairs = unpackr.unpack(this.propertyData!.subarray(start, end));
      if (!Array.isArray(pairs) || pairs.length % 2) throw new Error("Invalid node property record");
      const entries: [string, unknown][] = [];
      const seen = new Set<number>();
      for (let i = 0; i < pairs.length; i += 2) {
        const key = pairs[i];
        if (!Number.isSafeInteger(key) || key < 0 || key >= this.propertyKeys!.length || seen.has(key)) {
          throw new Error("Invalid node property key index");
        }
        seen.add(key);
        const name = this.propertyKeys![key];
        if (!RESERVED_PROPERTIES.has(name)) entries.push([name, pairs[i + 1]]);
      }
      const own = Object.fromEntries(entries);
      if (this.propertyRecords && entries.every(([, value]) => value === null || typeof value !== "object")) {
        this.primitivePropertyRecords.set(record, { ...own });
      }
      this.propertyCache.set(id, own);
    }
    return this.propertyCache.get(id);
  }

  private readonly header: Header;
  private readonly bytes: Uint8Array;
  private readonly release?: DecisionGraphRelease;
  private readonly labelId?: string;
  private readonly nodeTemplate: UnsignedColumn;
  private readonly nodeInstance: SignedColumn;
  private readonly nodeReason: UnsignedColumn;
  private readonly nodeJustification: UnsignedColumn;
  /** Per node: its value record (0 none, else index + 1 into valueOffsets/valueData, each distinct record once). */
  private readonly nodeValue: UnsignedColumn;
  private readonly valueOffsets: UnsignedColumn;
  private readonly valueData: Uint8Array;
  private readonly instanceParent: SignedColumn;
  private readonly instanceEntity: UnsignedColumn;
  private readonly instanceIndex: SignedColumn;
  /** The edges as CSR (each node's targets ascending), decoded from the edge sections when first walked. */
  private csr?: { offsets: Uint32Array; targets: Uint32Array; sources: Uint8Array };
  private readonly edgeSection: (name: string) => Uint8Array;
  private readonly reasonOffsets: UnsignedColumn;
  private readonly reasonData: Uint8Array;
  private readonly unpacked: (DecisionGraphNodeValues | null | undefined)[];
  /** Value records whose values are all primitives, decoded once and shared by the nodes that have them. */
  private primitiveRecords?: ([number, ...unknown[]] | undefined)[];
  private readonly objects: (NodeObject | undefined | typeof UNSET)[];
  private reasons?: (DecisionGraphReason | undefined)[];
  private instancePaths?: (string | undefined)[];
  private paths?: (string | undefined)[];
  private idsByKey?: Map<number, number>;
  private templateIds?: Map<string, number>;
  private instanceIds?: Map<string, number>;
  private outOffsets?: Uint32Array;
  /** Where an edge between two container nodes comes in the rule graph's edges (when the container has them). */
  private edgeRank?: (from: number, to: number) => number;
  private outTargets?: Uint32Array;
  private inOffsets?: Uint32Array;
  private inSources?: Uint32Array;
  private graphLabel?: Record<string, unknown>;
  /** What the graph was made with, for clone(). */
  private readonly madeWith: DecisionGraphOptions;
  /** The rule data is the graph's own (a release's compiled graph), not a release it was given. */
  private readonly ownRuleData: boolean;
  private edit?: DecisionGraphEdits;
  private ruleEdgeLabels?: Map<number, Map<number, unknown>>;
  /** The container's template edges that only an instance edge implies (from * size + to), from buildAdjacency. */
  private impliedPairs?: Set<number>;
  /** Template indexes for guids the container doesn't have (nodes added since), after its own. */
  private addedTemplates?: Map<string, number>;
  private editRevision = 0;

  /** Changes with every edit (nodes, edges, node data, label): caches made over a graph compare it. */
  get revision(): number {
    return this.editRevision;
  }

  /**
   * A graph from its container's bytes, or a new, empty graph to build with setNode/setEdge
   * (`new DecisionGraph()`, `new DecisionGraph({ multigraph: true })`).
   */
  constructor(container?: Uint8Array | DecisionGraphGraphOptions, options: DecisionGraphOptions = {}) {
    if (!(container instanceof Uint8Array)) container = emptyDecisionGraph(container ?? {});
    this.madeWith = options;
    if (!isDecisionGraphBytes(container)) throw new Error("Not a decision graph");
    // sections are 8-byte aligned within the container: start it on an aligned offset so every column is a view
    const bytes = container.byteOffset % 8 === 0 ? container : new Uint8Array(container);
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const headerLength = view.getUint32(4, true);
    if (headerLength > bytes.length - 8) throw new Error("Truncated decision graph header");
    const header = unpackr.unpack(bytes.subarray(8, 8 + headerLength)) as Header;
    const minReaderVersion = header.minReaderVersion ?? 1;
    if (
      typeof header.formatVersion !== "number" || !Number.isSafeInteger(header.formatVersion) || header.formatVersion < 1 ||
      !Number.isSafeInteger(minReaderVersion) || minReaderVersion < 1 || minReaderVersion > header.formatVersion
    ) {
      throw new UnsupportedDecisionGraphFormatError(header.formatVersion, DECISION_GRAPH_FORMAT_VERSION);
    }
    if (minReaderVersion > DECISION_GRAPH_FORMAT_VERSION) {
      throw new UnsupportedDecisionGraphFormatError(header.formatVersion, DECISION_GRAPH_FORMAT_VERSION);
    }
    header.sections = readSections(header.sections as unknown as SectionEntry[]);
    const dataStart = Math.ceil((8 + headerLength) / 8) * 8;
    const sections = new Map<string, Section>();
    for (const section of header.sections) {
      if (typeof section.name !== "string" || sections.has(section.name) ||
          !Number.isSafeInteger(section.offset) || section.offset < 0 || section.offset % 8 !== 0 ||
          !Number.isSafeInteger(section.length) || section.length < 0 ||
          section.offset + section.length > bytes.length - dataStart) {
        throw new Error("Invalid decision graph section table");
      }
      sections.set(section.name, section);
    }
    const section = (name: string) => {
      const found = sections.get(name);
      if (!found) throw new Error(`Decision graph is missing '${name}'`);
      return { offset: bytes.byteOffset + dataStart + found.offset, length: found.length };
    };
    const integer = (name: string, signed: boolean) => {
      const { offset, length } = section(name);
      const width = sections.get(name)!.width;
      const Type = columnType(signed, width);
      if (length % width) throw new Error(`Invalid integer section: ${name}`);
      return new Type(bytes.buffer as ArrayBuffer, offset, length / width);
    };
    const u32 = (name: string) => integer(name, false) as UnsignedColumn;
    const i32 = (name: string) => integer(name, true) as SignedColumn;
    const u8 = (name: string) => {
      const { offset, length } = section(name);
      return new Uint8Array(bytes.buffer, offset, length);
    };

    this.bytes = bytes;
    this.header = header;
    this.id = header.id;
    this.session = header.session;
    this.debug = header.debug;
    this.options = header.options;
    this.templates = header.templates;
    this.listedCount = header.nodeCount;
    this.nodeTemplate = u32("nodeTemplate");
    this.nodeInstance = i32("nodeInstance");
    this.nodeReason = u32("nodeReason");
    this.nodeJustification = u32("nodeJustification");
    this.nodeValue = u32("nodeValue");
    this.valueOffsets = u32("valueOffsets");
    this.valueData = u8("valueData");
    this.instanceParent = i32("instanceParent");
    this.instanceEntity = u32("instanceEntity");
    this.instanceIndex = i32("instanceIndex");
    // edges are decoded when first walked (decodeEdges)
    this.edgeSection = u8;
    this.reasonOffsets = u32("reasonOffsets");
    this.reasonData = u8("reasonData");
    this.size = this.nodeTemplate.length;
    this.unpacked = new Array(this.listedCount);
    this.objects = new Array(this.size).fill(UNSET);

    const release = options.release
      ? "ruleNode" in options.release
        ? options.release
        : decisionGraphRelease(options.release)
      : sections.has("ruleData")
        ? this.embeddedRelease(u32("ruleOffsets"), u8("ruleData"), options.documents)
        : undefined;
    this.ownRuleData = !options.release && sections.has("ruleData");
    this.minimal = options.minimal ?? !release;
    // the minimal graph carries no rule data
    this.release = this.minimal ? undefined : (release as DecisionGraphRelease | undefined);
    this.labelId = options.id;
    const propertySections = ["propertyKeys", "nodePropertyOffsets", "nodePropertyData"];
    if (["propertyFlags", "propertyNodes", "nodePropertyRecords", ...propertySections].some((name) => sections.has(name))) {
      if (!propertySections.every((name) => sections.has(name))) throw new Error("Incomplete node property sections");
      const keys = unpackr.unpack(u8("propertyKeys"));
      if (!Array.isArray(keys) || keys.some((key) => typeof key !== "string") || new Set(keys).size !== keys.length) {
        throw new Error("Invalid node property dictionary");
      }
      this.propertyKeys = keys;
      if (sections.has("propertyFlags")) {
        this.propertyFlags = u8("propertyFlags");
        if (this.propertyFlags.length !== keys.length) throw new Error("Invalid node property flags");
      }
      this.propertyOffsets = u32("nodePropertyOffsets");
      this.propertyData = u8("nodePropertyData");
      if (sections.has("propertyNodes")) {
        this.propertyNodes = u32("propertyNodes");
        if (this.propertyNodes.some((id, i, ids) => id >= this.listedCount || (i > 0 && id <= ids[i - 1]))) {
          throw new Error("Invalid property node indexes");
        }
      }
      const rows = this.propertyNodes?.length ?? this.listedCount;
      if (sections.has("nodePropertyRecords")) {
        this.propertyRecords = u32("nodePropertyRecords");
        if (this.propertyRecords.length !== rows || this.propertyRecords.some((ref) => ref >= this.propertyOffsets!.length)) {
          throw new Error("Invalid node property references");
        }
      } else if (this.propertyOffsets.length !== rows + 1) {
        throw new Error("Invalid node property offsets");
      }
      if (this.propertyOffsets.length === 0 || this.propertyOffsets[0] !== 0 ||
          this.propertyOffsets[this.propertyOffsets.length - 1] !== this.propertyData.length ||
          this.propertyOffsets.some((offset, i, offsets) => offset > this.propertyData!.length || (i > 0 && offset < offsets[i - 1]))) {
        throw new Error("Invalid node property offsets");
      }
    }
  }

  private get edgeOffsets(): Uint32Array {
    return this.decodeEdges().offsets;
  }

  private get edgeTargets(): Uint32Array {
    return this.decodeEdges().targets;
  }

  private get edgeSources(): Uint8Array {
    return this.decodeEdges().sources;
  }

  /** The edges as CSR, each node's targets ascending by node id, from the edge sections. */
  private decodeEdges() {
    if (this.csr) return this.csr;
    const size = this.nodeTemplate.length;
    const offsets = new Uint32Array(size + 1);
    // per node, its targets grouped by template, each group's instances as differences (see the engine's
    // EdgeSections); a target is the node with that template and instance
    const groupCounts = this.sectionView("edgeGroupCounts", Uint32Array)!;
    const groupTemplates = this.sectionView("edgeGroupTemplates", Int32Array)!;
    const groupSizes = this.sectionView("edgeGroupSizes", Uint32Array)!;
    const instances = this.sectionView("edgeTargetInstances", Int32Array)!;
    const groupedSources = this.edgeSection("edgeSources");
    const nodeOf = this.nodeLookup();
    const targets = new Uint32Array(instances.length);
    const sources = new Uint8Array(instances.length);
    // targets with their sources packed (id * 8 + flags), sorted per node into ascending ids
    const packed = new Uint32Array(instances.length);
    for (let from = 0, group = 0, p = 0; from < size; from++) {
      const start = p;
      let template = this.nodeTemplate[from];
      for (let g = 0; g < groupCounts[from]; g++, group++) {
        template += groupTemplates[group];
        let instance = this.nodeInstance[from];
        for (let e = 0; e < groupSizes[group]; e++, p++) {
          instance += instances[p];
          const to = nodeOf(template, instance);
          if (to < 0) throw new Error(`Decision graph edge to a node it doesn't have (template ${template}, instance ${instance})`);
          packed[p] = to * 8 + groupedSources[p];
        }
      }
      offsets[from + 1] = p;
      packed.subarray(start, p).sort();
    }
    for (let p = 0; p < packed.length; p++) {
      targets[p] = packed[p] >>> 3;
      sources[p] = packed[p] & 7;
    }
    this.csr = { offsets, targets, sources };
    return this.csr;
  }

  /** The node with a template and instance (-1 global), or -1: an open-addressing table over node ids. */
  private nodeLookup(): (template: number, instance: number) => number {
    const size = this.nodeTemplate.length;
    let capacity = 16;
    while (capacity < size * 2) capacity *= 2;
    const mask = capacity - 1;
    const table = new Int32Array(capacity).fill(-1);
    const hash = (template: number, instance: number) =>
      (Math.imul(template, 0x9e3779b1) ^ Math.imul(instance + 1, 0x85ebca77)) & mask;
    for (let id = 0; id < size; id++) {
      let slot = hash(this.nodeTemplate[id], this.nodeInstance[id]);
      while (table[slot] !== -1) slot = (slot + 1) & mask;
      table[slot] = id;
    }
    return (template, instance) => {
      for (let slot = hash(template, instance); table[slot] !== -1; slot = (slot + 1) & mask) {
        const id = table[slot];
        if (this.nodeTemplate[id] === template && this.nodeInstance[id] === instance) return id;
      }
      return -1;
    };
  }

  /**
   * The rule data a graph carries (ruleOffsets/ruleData: each listed node's rule graph value, MessagePack). A guid's
   * rule node is its global node's, as a rule graph's node by guid; each is unpacked when first read.
   */
  private embeddedRelease(offsets: UnsignedColumn, data: Uint8Array, documents: any): DecisionGraphRelease {
    let globalNodes: Map<string, number> | undefined;
    const rules = new Map<number, unknown>();
    return {
      documents,
      ruleNode: (guid: string) => {
        if (!globalNodes) {
          globalNodes = new Map();
          for (let id = 0; id < this.listedCount; id++) {
            if (this.nodeInstance[id] < 0) globalNodes.set(this.templates[this.nodeTemplate[id]], id);
          }
        }
        const id = globalNodes.get(guid);
        if (id === undefined || offsets[id] === offsets[id + 1]) return undefined;
        if (!rules.has(id)) rules.set(id, unpackr.unpack(data.subarray(offsets[id], offsets[id + 1])));
        return rules.get(id) as any;
      },
    };
  }

  /**
   * A decision graph from any form a graph comes in; an existing graph is returned as it is.
   * - engine bytes, or a saved/response form (`{ decisionGraph, encoding }`, deflated, bytes or base64)
   * - legacy JSON (`{ nodes: [{ v, value }], edges: [{ v, w, value }] }`: exports, graphs saved before decision
   *   graphs), also as a JSON string or deflated JSON bytes, and a graphlib graph object; these are written as a
   *   decision graph (legacyGraphToDecisionGraph: a session's graph when it has instance paths), once per object
   */
  static from(input: DecisionGraphInput | GraphJson | string | Record<string, unknown>, options?: DecisionGraphOptions): DecisionGraph {
    if (input instanceof DecisionGraph) return input;
    if (input === null || input === undefined) throw new Error("A graph is required");
    if (typeof input === "string") return DecisionGraph.from(JSON.parse(input), options);
    if (isStoredDecisionGraph(input)) return new DecisionGraph(toBytes(input), options);
    if (input instanceof Uint8Array || input instanceof ArrayBuffer) {
      const bytes = input instanceof ArrayBuffer ? new Uint8Array(input) : input;
      if (isDecisionGraphBytes(bytes)) return new DecisionGraph(bytes, options);
    }
    const cached = !options && typeof input === "object" ? fromLegacy.get(input) : undefined;
    if (cached) return cached;
    const json = legacyGraphJson(input);
    const session = json.nodes.some((node) => String(node.v).includes("/"));
    const graph = new DecisionGraph(legacyGraphToDecisionGraph(json as any, { session, ruleData: true }), options);
    if (!options && typeof input === "object" && Object.isExtensible(input)) fromLegacy.set(input, graph);
    return graph;
  }


  /**
   * The container's bytes (what the engine produced; deflate them to save). With edits, a new container written from
   * the graph's nodes, edges and label, keeping what the container carried for the nodes and edges still in it: its
   * header (id, debug, entityStructure), persistent state, validations, node properties and edge sources. Unknown
   * sections aren't kept.
   */
  toBytes(): Uint8Array {
    if (!this.edited()) return this.bytes;
    return this.writeEdited();
  }

  /**
   * This graph when it has no edits, otherwise a graph read from its toBytes() (with the same options): what the
   * helpers that read a container's own tables (edgeArrays, edgeEntries, properties, the legacy graph, reports) need.
   * Its ids are its own: map them by path (deref, then ref).
   */
  toPacked(): DecisionGraph {
    return this.edited() ? new DecisionGraph(this.writeEdited(), this.madeWith) : this;
  }

  /**
   * Whether every node and edge is in the container: no nodes or edges added or removed (or edge-only nodes listed)
   * since it was read.
   */
  get packed(): boolean {
    const edit = this.edit;
    return !edit || (edit.added.length === 0 && edit.removed.size === 0 && edit.listed.size === 0 &&
      edit.edgesAdded.size === 0 && edit.edgesRemoved.size === 0);
  }

  /**
   * One more than the largest id the graph can have (the container's nodes, then those added): the length for
   * typed-array masks over node ids. ids() lists the graph's nodes.
   */
  idBound(): number {
    return this.size + (this.edit?.added.length ?? 0);
  }

  /** Whether the node was added in the edit layer (it isn't in the container, so it carries its own path fields). */
  isAdded(id: number): boolean {
    return id >= this.size && id < this.idBound();
  }

  /** Whether the id is a node in the graph: listed, or an edge-only target, and not removed. */
  private exists(id: number): boolean {
    return Number.isInteger(id) && id >= 0 && id < this.idBound() && !this.edit?.removed.has(id);
  }

  /** Whether the node's data was written (or changed in place) since the container: what toBytes writes as new. */
  private isChanged(id: number): boolean {
    const edit = this.edit;
    const data = edit?.data.get(id);
    if (!edit || !data) return false;
    if (id >= this.listedCount || edit.changed.has(id)) return true;
    return JSON.stringify(data) !== JSON.stringify(this.view(id));
  }

  /** The edited graph as a new container (toBytes), keeping what the container carried for what's still in it. */
  private writeEdited(): Uint8Array {
    const edit = this.edit!;
    const ruleData = this.hasRuleData;
    const listed = this.ids();
    // edge-only nodes stay (state and validations can refer to them), after the listed ones
    const kept = [...listed];
    for (let id = this.listedCount; id < this.size; id++) if (!edit.removed.has(id) && !edit.listed.has(id)) kept.push(id);

    // an instance keeps its index as the container has it (an external id can look like a position)
    let indexes: Map<string, string | number> | undefined;
    const tables = pathTables((instancePath) => {
      if (!indexes) {
        indexes = new Map();
        for (let i = 0; i < this.instanceParent.length; i++) indexes.set(this.instancePathOf(i), this.instanceIndexOf(i));
      }
      return indexes.get(instancePath);
    });
    const newIds = new Map<number, number>();
    for (const id of kept) newIds.set(id, tables.add(this.deref(id)));
    const templateEntity = Uint8Array.from(tables.templates, (guid, t) => {
      const known = this.templateIndex(guid);
      return tables.entityTemplates.has(t) || (known < this.templates.length && this.header.templateEntity[known] === 1) ? 1 : 0;
    });

    // edges: the container's (each with its sources), less those removed; set again, an edge is solved as well as
    // what it was; a new one is solved
    const bound = this.idBound();
    const edges: [number, number, number][] = [];
    const written = new Set<number>();
    for (const [from, to, sources] of this.containerEdgeEntries()) {
      if (edit.removed.has(from) || edit.removed.has(to)) continue;
      let flags = sources;
      if (edit.edgesRemoved.get(from)?.has(to)) flags &= ~EDGE_FROM_STATE;
      if (edit.edgesAdded.get(from)?.has(to)) flags |= EDGE_FROM_STATE;
      if (!flags) continue;
      edges.push([newIds.get(from)!, newIds.get(to)!, flags]);
      if (flags & EDGE_FROM_STATE) written.add(from * bound + to);
    }
    // in graph order (toJSON's): the order the rule edges keep. An implied template edge isn't written (the reader
    // implies it again) unless it has a label of its own
    const ordered: [number, number][] = [];
    for (const [from, to, implied] of this.liveEdges()) {
      const key = from * bound + to;
      if (!written.has(key)) {
        if (implied && !edit.edgeLabels.get(from)?.has(to)) continue;
        edges.push([newIds.get(from)!, newIds.get(to)!, EDGE_FROM_STATE]);
        written.add(key);
      }
      ordered.push([from, to]);
    }
    const labels = ordered.map(([from, to]) => {
      const label = this.edge(from, to);
      return label !== undefined ? { value: label } : undefined;
    });
    const writeRuleEdges = ruleData || !!this.sectionView("ruleEdges", Uint32Array) || labels.some((label) => label !== undefined);

    const state = this.sectionView("nodeState", Uint32Array);
    const stateOffsets = this.sectionView("stateOffsets", Uint32Array);
    const stateData = this.sectionView("stateData", Uint8Array);
    const validations = this.validationRecords()?.flatMap((validation) => {
      if (edit.removed.has(validation.node)) return [];
      const attributes = validation.attributes.filter((id) => !edit.removed.has(id)).map((id) => newIds.get(id)!);
      return [{ ...validation, node: newIds.get(validation.node)!, attributes }];
    });
    const label = edit.label ? edit.label.value : this.header.ruleGraphValue;
    return writeDecisionGraph({
      id: this.id,
      session: this.session,
      debug: this.debug,
      options: ruleData ? (this.options ?? { directed: true, multigraph: false, compound: false }) : this.options,
      templates: tables.templates,
      templateEntity,
      instances: tables.instances,
      nodes: tables.nodes,
      nodeCount: listed.length,
      values: listed.map((id) => (this.isChanged(id) ? this.values(id) : this.baseValues(id))),
      nodeProperties: listed.map((id) => this.propertiesToWrite(id, ruleData)),
      persistentProperties: this.persistentProperties,
      edges,
      nodeStates: state && kept.map((id) => {
        const record = id < this.size ? state[id] : 0;
        return record ? stateData!.subarray(stateOffsets![record - 1], stateOffsets![record]) : undefined;
      }),
      validations,
      ...(ruleData ? {
        // listed nodes are the rule graph's nodes, in its order: as they were, unless changed
        ruleNodes: listed.map((id) => (this.isChanged(id) ? this.plain(id) : this.ruleNodeRecord(id).value)),
        ruleNodeExtras: listed.map((id) => {
          if (id >= this.listedCount) return undefined;
          const { v: _v, value: _value, ...extra } = this.ruleNodeRecord(id) as Record<string, unknown>;
          return Object.keys(extra).length > 0 ? extra : undefined;
        }),
      } : {}),
      ...(writeRuleEdges ? {
        ruleEdges: ordered.map(([from, to]) => [newIds.get(from)!, newIds.get(to)!] as [number, number]),
        ruleEdgeLabels: labels,
      } : {}),
      ...(label !== undefined ? { ruleGraphValue: label } : {}),
      ...(this.header.entityStructure !== undefined ? { entityStructure: this.header.entityStructure } : {}),
    });
  }

  /**
   * The properties toBytes writes for a node: the container's, unless its data changed. Then its own properties
   * still in its data, and (when the graph doesn't carry rule data, where the rest of the data goes) every other field
   * that isn't what the node would have anyway: its rule data or, for an instance, its template's.
   */
  private propertiesToWrite(id: number, ruleData: boolean): Record<string, unknown> | undefined {
    if (!this.isChanged(id)) return id < this.listedCount ? this.containerProperties(id) : undefined;
    const data = this.plain(id) ?? {};
    const own = id < this.listedCount ? this.containerProperties(id) : undefined;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(own ?? {})) if (data[key] !== undefined) out[key] = data[key];
    if (!ruleData) {
      const guid = this.guid(id);
      const template = this.isGlobal(id) ? undefined : this.ref(guid);
      const inherited = template !== undefined ? this.plain(template) : this.minimal ? undefined : this.release?.ruleNode(guid);
      for (const [key, value] of Object.entries(data)) {
        if (RESERVED_PROPERTIES.has(key) || value === undefined || key in out) continue;
        if (JSON.stringify(value) !== JSON.stringify(inherited?.[key])) out[key] = value;
      }
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }

  /** Whether the graph has edits: nodes, edges or its label changed, or node data written (or changed in place). */
  private edited(): boolean {
    const edit = this.edit;
    if (!edit) return false;
    if (edit.shape > 0 || edit.changed.size > 0 || edit.label) return true;
    // data only read through node() can still have been changed in place (node.usedIn.push(...))
    for (const [id, data] of edit.data) {
      if (!edit.changed.has(id) && JSON.stringify(data) !== JSON.stringify(this.view(id))) return true;
    }
    return false;
  }

  /** An independent copy: the same container, with this graph's edits copied. */
  clone(): DecisionGraph {
    const copy = new DecisionGraph(this.bytes, this.madeWith);
    // the container's decodes never change (they're made from the container alone, and writes go to a node's own
    // copy), so the clone shares them
    (copy as any).objects = this.objects;
    (copy as any).unpacked = this.unpacked;
    const edit = this.edit;
    if (edit) {
      const copyMap = <K, V>(map: Map<K, V>, value: (v: V) => V) => new Map([...map].map(([k, v]) => [k, value(v)]));
      copy.edit = {
        removed: new Set(edit.removed),
        added: [...edit.added],
        addedIds: new Map(edit.addedIds),
        listed: new Set(edit.listed),
        order: [...edit.order],
        data: copyMap(edit.data, copyValue),
        changed: new Set(edit.changed),
        proxies: new Map(),
        edgesAdded: copyMap(edit.edgesAdded, (to) => copyMap(to, copyValue)),
        edgesAddedIn: copyMap(edit.edgesAddedIn, (from) => new Set(from)),
        edgesRemoved: copyMap(edit.edgesRemoved, (to) => new Set(to)),
        edgeLabels: copyMap(edit.edgeLabels, (to) => copyMap(to, copyValue)),
        edgeLog: [...edit.edgeLog],
        label: edit.label && { value: copyValue(edit.label.value) },
        shape: edit.shape,
        views: new Map(),
      };
    }
    copy.editRevision = this.editRevision;
    return copy;
  }

  /** The graph as legacy JSON (`{ options, value, nodes, edges }`), for external outputs that still take JSON. */
  toJSON(): GraphJson {
    const label = this.label();
    return {
      options: { directed: true, multigraph: false, compound: false, ...(this.options ?? {}) },
      ...(label !== undefined ? { value: label } : {}),
      nodes: this.ids().map((id) => ({ v: this.deref(id), value: this.plain(id) })),
      edges: this.edgeRecords(),
    };
  }

  /**
   * The edges as legacy records, in the order they were added (graphlib's edges()): the container's in its rule edge
   * order, then the added ones. Each node's successors and predecessors keep their order through a write and read.
   */
  private edgeRecords(): { v: string; w: string; value?: unknown }[] {
    return this.liveEdges().map(([from, to]) => {
      const label = this.edge(from, to);
      return { v: this.deref(from), w: this.deref(to), ...(label !== undefined ? { value: label } : {}) };
    });
  }

  /**
   * Every edge, in order (edgeRecords'), with whether it's only there because an instance edge implies it: the
   * container's (in its rule edge order), then the added ones, then template edges implied by edits.
   */
  private liveEdges(): [number, number, boolean][] {
    this.buildAdjacency();
    const edit = this.edit;
    const out = this.outOffsets!, targets = this.outTargets!;
    const edited = !this.packed;
    const base: [number, number, number, boolean][] = [];
    for (let from = 0; from < this.size; from++) {
      if (edit?.removed.has(from)) continue;
      const removed = edit?.edgesRemoved.get(from);
      for (let p = out[from]; p < out[from + 1]; p++) {
        const to = targets[p];
        const implied = this.impliedPairs!.has(from * this.size + to);
        if (edit?.removed.has(to)) continue;
        if (implied ? edited && !this.implies(from, to) : removed?.has(to)) continue;
        base.push([from, to, this.edgeRank?.(from, to) ?? 0, implied]);
      }
    }
    // (a stable sort: each node's successors keep their order)
    if (this.edgeRank) base.sort((a, b) => a[2] - b[2] || a[0] - b[0]);
    const edges: [number, number, boolean][] = base.map(([from, to, , implied]) => [from, to, implied]);
    if (!edit || !edited) return edges;
    const bound = this.idBound();
    const seen = new Set(edges.map(([from, to]) => from * bound + to));
    if (edit.edgeLog.length > 0) {
      // an edge added more than once (removed between) takes its last place
      const added: [number, number, boolean][] = [];
      for (let i = edit.edgeLog.length - 2; i >= 0; i -= 2) {
        const from = edit.edgeLog[i], to = edit.edgeLog[i + 1];
        if (!edit.edgesAdded.get(from)?.has(to) || edit.removed.has(from) || edit.removed.has(to)) continue;
        if (seen.has(from * bound + to)) continue;
        seen.add(from * bound + to);
        added.push([from, to, false]);
      }
      edges.push(...added.reverse());
    }
    for (const [from, to] of this.impliedByEdits()) {
      if (seen.has(from * bound + to)) continue;
      seen.add(from * bound + to);
      edges.push([from, to, true]);
    }
    return edges;
  }

  /**
   * A new graph containing exactly these node IDs, in their original order, and edges between them.
   * IDs are remapped; use ref(path) on the result. Ancestor instances needed for paths are retained, not their nodes.
   * State and validations are restricted to selected nodes. This is a projection, not a full session checkpoint:
   * session-wide entityStructure and unknown extension sections are omitted.
   */
  filterByNodes(nodeIds: Iterable<number>): DecisionGraph {
    if (this.edited()) {
      // the edited graph written out, its nodes found there by path
      const packed = this.toPacked();
      const ids: number[] = [];
      for (const id of nodeIds) {
        if (!this.exists(id)) throw new RangeError(`Invalid decision graph node ID: ${id}`);
        ids.push(packed.ref(this.deref(id))!);
      }
      return packed.filterByNodes(ids);
    }
    const selected = new Set<number>();
    for (const id of nodeIds) {
      if (!Number.isInteger(id) || id < 0 || id >= this.size) throw new RangeError(`Invalid decision graph node ID: ${id}`);
      selected.add(id);
    }
    const ids = [...selected].sort((a, b) => a - b);
    const remap = new Map(ids.map((id, index) => [id, index]));
    const templates: string[] = [];
    const templateEntity: number[] = [];
    const templateIds = new Map<number, number>();
    const instances: DecisionGraphParts["instances"] = [];
    const instanceIds = new Map<number, number>();
    const instance = (old: number): number => {
      if (old < 0) return -1;
      const known = instanceIds.get(old);
      if (known !== undefined) return known;
      const parent = instance(this.instanceParent[old]);
      const id = instances.length;
      instances.push({ parent, entity: this.header.entityNames[this.instanceEntity[old]], index: this.instanceIndexOf(old) });
      instanceIds.set(old, id);
      return id;
    };
    const nodes = ids.map((id) => {
      const old = this.nodeTemplate[id];
      let template = templateIds.get(old);
      if (template === undefined) {
        template = templates.length;
        templateIds.set(old, template);
        templates.push(this.templates[old]);
        templateEntity.push(this.header.templateEntity[old]);
      }
      return { template, instance: instance(this.nodeInstance[id]) };
    });
    const listed = ids.filter((id) => this.isListed(id));
    const edges: DecisionGraphParts["edges"] = [];
    for (const [from, to, sources] of this.containerEdgeEntries()) {
      if (selected.has(from) && selected.has(to)) edges.push([remap.get(from)!, remap.get(to)!, sources]);
    }
    const hasRules = this.hasRuleData;
    const ruleEdges = hasRules ? [...this.ruleEdgeRecords()].filter(edge => selected.has(this.ref(edge.v)!) && selected.has(this.ref(edge.w)!)) : undefined;
    const state = this.sectionView("nodeState", Uint32Array);
    const stateOffsets = this.sectionView("stateOffsets", Uint32Array);
    const stateData = this.sectionView("stateData", Uint8Array);
    const validations = this.validations()?.flatMap((validation) => {
      const path = validation.parent ? `${validation.parent}/${validation.id}` : validation.id;
      const node = remap.get(this.ref(path)!);
      if (node === undefined) return [];
      return [{ ...validation, node, attributes: validation.attributes.flatMap((path) => {
        const id = remap.get(this.ref(path)!);
        return id === undefined ? [] : [id];
      }) }];
    });
    const bytes = writeDecisionGraph({
      id: this.id, session: this.session, debug: this.debug, options: this.options,
      templates, templateEntity: Uint8Array.from(templateEntity), instances, nodes, nodeCount: listed.length,
      values: listed.map((id) => {
        const template = this.isGlobal(id) ? undefined : this.ref(this.guid(id));
        return template !== undefined && !selected.has(template)
          ? { ...this.values(template), ...this.values(id) } : this.values(id);
      }),
      nodeProperties: listed.map((id) => {
        const template = this.isGlobal(id) ? undefined : this.ref(this.guid(id));
        const inherited = template !== undefined && !selected.has(template) ? this.containerProperties(template) : undefined;
        return inherited ? { ...inherited, ...this.containerProperties(id) } : this.containerProperties(id);
      }),
      persistentProperties: this.persistentProperties, edges,
      nodeStates: state && ids.map((id) => {
        const record = state[id];
        return record ? stateData!.subarray(stateOffsets![record - 1], stateOffsets![record]) : undefined;
      }),
      validations,
      ruleNodes: hasRules ? listed.map(id => this.ruleNodeRecord(id).value) : undefined,
      ruleNodeExtras: hasRules ? listed.map((id) => {
        const { v, value, ...extra } = this.ruleNodeRecord(id) as Record<string, unknown>;
        if (typeof extra.parent === "string" && !selected.has(this.ref(extra.parent)!)) delete extra.parent;
        return Object.keys(extra).length ? extra : undefined;
      }) : undefined,
      ruleEdges: ruleEdges?.map((edge) => [remap.get(this.ref(edge.v)!)!, remap.get(this.ref(edge.w)!)!]),
      ruleEdgeLabels: ruleEdges?.map(({ v, w, ...label }) => Object.keys(label).length ? label : undefined),
      ruleGraphValue: this.header.ruleGraphValue,
    });
    return DecisionGraph.from(bytes, { release: this.release, minimal: this.minimal, id: this.labelId });
  }

  // ---- ids <-> paths ----

  /**
   * The node id for a path (or a global guid), or undefined. Paths aren't materialised for this: the guid and the
   * instance path are looked up in their own (small) tables, then the node by (instance, template).
   */
  ref(path: string): number | undefined {
    const edit = this.edit;
    if (edit) {
      const added = edit.addedIds.get(path);
      if (added !== undefined && !edit.removed.has(added)) return added;
      const id = this.baseRef(path);
      return id === undefined || edit.removed.has(id) ? undefined : id;
    }
    return this.baseRef(path);
  }

  private baseRef(path: string): number | undefined {
    if (!this.idsByKey) {
      this.templateIds = new Map(this.templates.map((guid, t) => [guid, t]));
      const instances = this.instanceParent.length;
      this.instanceIds = new Map();
      for (let i = 0; i < instances; i++) this.instanceIds.set(this.instancePathOf(i), i);
      const templates = this.templates.length;
      this.idsByKey = new Map();
      // first id wins, as graphlib kept one node per path
      for (let id = this.size - 1; id >= 0; id--) {
        this.idsByKey.set((this.nodeInstance[id] + 1) * templates + this.nodeTemplate[id], id);
      }
    }
    const cut = path.lastIndexOf("/");
    const template = this.templateIds!.get(cut < 0 ? path : path.slice(cut + 1));
    if (template === undefined) return undefined;
    const instance = cut < 0 ? -1 : this.instanceIds!.get(path.slice(0, cut));
    if (instance === undefined) return undefined;
    return this.idsByKey.get((instance + 1) * this.templates.length + template);
  }

  /** The node's path, e.g. `employees/1/<guid>`, or the guid for a global node. */
  deref(id: number): string {
    if (id >= this.size) return this.edit!.added[id - this.size];
    const instance = this.nodeInstance[id];
    if (instance < 0) return this.guid(id);
    if (!this.paths) this.paths = new Array(this.size);
    return (this.paths[id] ??= `${this.instancePathOf(instance)}/${this.guid(id)}`);
  }

  // ---- what a node is ----

  /** The rule graph node guid (template) behind a node. */
  guid(id: number): string {
    if (id >= this.size) return addedPath(this.deref(id)).guid;
    return this.templates[this.nodeTemplate[id]];
  }

  /**
   * Index into the release's rule graph nodes (engine GraphNodeId): for a node added since, its guid's, or -1 when the
   * container has no node with that guid.
   */
  template(id: number): number {
    if (id < this.size) return this.nodeTemplate[id];
    const t = this.templateIndex(this.guid(id));
    return t < this.templates.length ? t : -1;
  }

  /** A guid's template index: the container's, or one after them for a guid only nodes added since have. */
  private templateIndex(guid: string): number {
    if (!this.templateIds) this.baseRef(guid);
    const known = this.templateIds!.get(guid);
    if (known !== undefined) return known;
    this.addedTemplates ??= new Map();
    let t = this.addedTemplates.get(guid);
    if (t === undefined) this.addedTemplates.set(guid, (t = this.templates.length + this.addedTemplates.size));
    return t;
  }

  /** The guid of a template index (templateIndex). */
  private templateGuid(t: number): string {
    if (t < this.templates.length) return this.templates[t];
    for (const [guid, index] of this.addedTemplates ?? []) if (index === t) return guid;
    throw new RangeError(`No template ${t}`);
  }

  /** The node's instance, or -1 for a global node (and for a node added since). */
  instance(id: number): number {
    return id >= this.size ? -1 : this.nodeInstance[id];
  }

  isGlobal(id: number): boolean {
    if (id >= this.size) return addedPath(this.deref(id)).instancePath === undefined;
    return this.nodeInstance[id] < 0;
  }

  /** A global copy of an entity attribute: the shared template, not an instance of it. */
  isHidden(id: number): boolean {
    if (id >= this.size) return this.plain(id)?.hidden === true;
    if (this.nodeInstance[id] >= 0) return false;
    if (this.header.templateEntity[this.nodeTemplate[id]] === 1) return true;
    // a graph saved before decision graphs only shows a template is an entity attribute when it saved one of its
    // instances: the global copy of an attribute of an entity with no rows is hidden as its rule data says
    return this.release?.ruleNode(this.guid(id))?.hidden === true;
  }

  /** Whether the node is in the graph with values of its own (false for nodes only reached as edge targets). */
  isListed(id: number): boolean {
    const edit = this.edit;
    if (id < this.listedCount) return id >= 0 && !edit?.removed.has(id);
    if (!edit || edit.removed.has(id)) return false;
    return id < this.size ? edit.listed.has(id) : id - this.size < edit.added.length;
  }

  /**
   * The graph's nodes (ids with values of their own), in order: the container's, then those added (or edge-only
   * nodes listed) since, as they were. Iterate this, not 0..listedCount, to see edits; size masks with idBound().
   */
  ids(): number[] {
    const edit = this.edit;
    const ids: number[] = [];
    for (let id = 0; id < this.listedCount; id++) if (!edit?.removed.has(id)) ids.push(id);
    if (edit) for (const id of edit.order) if (!edit.removed.has(id)) ids.push(id);
    return ids;
  }

  /** How many nodes the graph has (ids()). */
  nodeCount(): number {
    const edit = this.edit;
    if (!edit) return this.listedCount;
    let count = this.listedCount + edit.order.length;
    for (const id of edit.removed) if (id < this.listedCount || id >= this.size || edit.listed.has(id)) count--;
    return count;
  }

  /** The node of the template's global copy (the template node), or undefined. */
  templateNode(id: number): number | undefined {
    return this.ref(this.guid(id));
  }

  /** The path of the entity instance the node belongs to, or undefined when global. */
  instancePath(id: number): string | undefined {
    if (id >= this.size) return addedPath(this.deref(id)).instancePath;
    const instance = this.nodeInstance[id];
    return instance < 0 ? undefined : this.instancePathOf(instance);
  }

  /** The instance's entity name, or undefined when global. */
  entity(id: number): string | undefined {
    if (id >= this.size) return addedPath(this.deref(id)).entity;
    const instance = this.nodeInstance[id];
    return instance < 0 ? undefined : this.header.entityNames[this.instanceEntity[instance]];
  }

  /** The instance's index: its external id (a string), or its 1-based position. */
  index(id: number): string | number | undefined {
    if (id >= this.size) return addedPath(this.deref(id)).index;
    const instance = this.nodeInstance[id];
    return instance < 0 ? undefined : this.instanceIndexOf(instance);
  }

  /** The path of the parent entity instance, if the node's instance has one. */
  parentPath(id: number): string | undefined {
    if (id >= this.size) return addedPath(this.deref(id)).parentPath;
    const instance = this.nodeInstance[id];
    if (instance < 0) return undefined;
    const parent = this.instanceParent[instance];
    return parent < 0 ? undefined : this.instancePathOf(parent);
  }

  // ---- values ----

  /** The node's own values, unpacked (and kept) on first read. */
  values(id: number): DecisionGraphNodeValues | undefined {
    const data = this.edit?.data.get(id);
    if (data) {
      const values: DecisionGraphNodeValues = {};
      if (data.input !== undefined) values.input = data.input;
      if (data.derived !== undefined) values.derived = data.derived;
      if (data.justification !== undefined) values.justification = data.justification;
      if (data.triggered !== undefined) values.triggered = data.triggered;
      if (data.debugInfo?.reason) values.reason = data.debugInfo.reason;
      return Object.keys(values).length > 0 ? values : undefined;
    }
    return this.baseValues(id);
  }

  /** The node's values as the container has them, unpacked (and kept) on first read. */
  private baseValues(id: number): DecisionGraphNodeValues | undefined {
    if (id >= this.listedCount) return undefined;
    const cached = this.unpacked[id];
    if (cached !== undefined) return cached ?? undefined;
    const values: DecisionGraphNodeValues = {};
    const record = this.nodeValue[id];
    if (record > 0) {
      const start = this.valueOffsets[record - 1];
      const end = this.valueOffsets[record];
      // [flags, ...present values]: flags mark presence, since a value can itself be null. Nodes share records: one
      // whose values are all primitives is decoded once (sharing it can't let one node's values change another's)
      let record_values = this.primitiveRecords?.[record - 1];
      if (record_values === undefined) {
        record_values = unpackr.unpack(this.valueData.subarray(start, end)) as [number, ...unknown[]];
        if (record_values.every((item) => item === null || typeof item !== "object")) {
          (this.primitiveRecords ??= [])[record - 1] = record_values;
        }
      }
      const flags = record_values[0] as number;
      const present = record_values;
      let at = 1;
      if (flags & VALUE_INPUT) values.input = present[at++];
      if (flags & VALUE_DERIVED) values.derived = present[at++];
      if (flags & VALUE_TRIGGERED) values.triggered = present[at++] as unknown[];
    }
    const justification = this.nodeJustification[id];
    if (justification > 0) values.justification = this.header.justifications[justification - 1];
    const reason = this.reasonOf(this.nodeReason[id]);
    if (reason) values.reason = reason;
    const result = Object.keys(values).length > 0 ? values : null;
    this.unpacked[id] = result;
    return result ?? undefined;
  }

  /** The node's resolved value: derived when derived, otherwise its input. */
  value(id: number): unknown {
    const values = this.values(id);
    return values?.derived !== undefined ? values.derived : values?.input;
  }

  /**
   * The node as an object, as the legacy graph showed it (made on first read and kept): with the release, rule data
   * merged in (the portal's debug graph); otherwise values and debug reasons (decisionapi's report graph). Instances
   * carry their template's fields plus id, path, index and parent_path. Undefined for edge-only targets.
   */
  node(id: number | undefined): NodeObject | undefined {
    if (id === undefined || !this.isListed(id)) return undefined;
    const edit = this.edits();
    let proxy = edit.proxies.get(id);
    if (!proxy) edit.proxies.set(id, (proxy = this.nodeProxy(id)));
    return proxy;
  }

  /**
   * The node id for a path, adding an empty node there when there isn't one (a path removed is a new node, at the
   * end). The one way a path becomes a node; everything else takes ids.
   */
  createRef(path: string): number {
    const existing = this.ref(path);
    if (existing !== undefined) {
      // a node only reached as an edge target becomes a node of its own, keeping its id and edges (graphlib had one
      // node, made by the edge)
      if (!this.isListed(existing)) this.list(existing);
      return existing;
    }
    const edit = this.edits();
    const id = this.size + edit.added.length;
    edit.added.push(path);
    edit.addedIds.set(path, id);
    edit.order.push(id);
    edit.data.set(id, {});
    edit.changed.add(id);
    edit.shape++;
    this.editRevision++;
    this.reindex(id, true);
    return id;
  }

  /** Make an edge-only node a node of its own, with no data. */
  private list(id: number) {
    const edit = this.edits();
    edit.listed.add(id);
    edit.order.push(id);
    edit.data.set(id, {});
    edit.changed.add(id);
    edit.shape++;
    this.editRevision++;
    this.reindex(id, true);
  }

  /**
   * Replace a node's data (ids only: createRef(path) for a node at a path). Returns the id. An edge-only node becomes
   * a node of its own.
   */
  setNode(id: number, data?: NodeObject): number {
    if (typeof id !== "number") throw new TypeError(`setNode takes a node id (createRef(path) for a path): ${String(id)}`);
    if (!this.exists(id)) throw new RangeError(`No decision graph node ${id}`);
    if (!this.isListed(id)) this.list(id);
    if (data !== undefined) {
      const edit = this.edits();
      // only the indexes of fields whose value changes
      const previous = this.plain(id);
      const changed = [...this.fieldIndexes.keys()].filter((field) => field !== "$key" && previous?.[field] !== data[field]);
      for (const field of changed) this.reindex(id, false, field);
      edit.data.set(id, data);
      edit.changed.add(id);
      this.editRevision++;
      for (const field of changed) this.reindex(id, true, field);
    }
    return id;
  }

  /** Remove a node (or an edge-only node) and its edges. */
  removeNode(id: number): this {
    if (!this.exists(id)) return this;
    const edit = this.edits();
    this.reindex(id, false);
    for (const to of [...this.successors(id)]) this.removeEdge(id, to);
    for (const from of [...this.predecessors(id)]) this.removeEdge(from, id);
    edit.removed.add(id);
    edit.data.delete(id);
    edit.changed.delete(id);
    edit.proxies.delete(id);
    if (id >= this.size) edit.addedIds.delete(this.deref(id));
    edit.shape++;
    this.editRevision++;
    return this;
  }

  /** The graph's edits, made on first use. */
  private edits(): DecisionGraphEdits {
    return (this.edit ??= {
      removed: new Set(), added: [], addedIds: new Map(), listed: new Set(), order: [], data: new Map(), changed: new Set(),
      proxies: new Map(), edgesAdded: new Map(), edgesAddedIn: new Map(), edgesRemoved: new Map(), edgeLabels: new Map(),
      edgeLog: [], shape: 0, views: new Map(),
    });
  }

  /**
   * The node as it is in the container (made once and kept), without edits: made from the container alone, so it can
   * be shared (clones share these) and compared against.
   */
  private original(id: number): NodeObject | undefined {
    if (id >= this.size) return undefined;
    const cached = this.objects[id];
    if (cached !== UNSET) return cached;
    const object = this.buildNode(id, false);
    this.objects[id] = object;
    return object;
  }

  /**
   * Whether an instance's node object is no longer the container's because its template's is edited (or its
   * template node removed or added): it's then made over the template's data as it is now (view).
   */
  private overEditedTemplate(id: number): boolean {
    const edit = this.edit;
    if (!edit || id >= this.listedCount || this.nodeInstance[id] < 0) return false;
    if (edit.data.size === 0 && edit.removed.size === 0 && edit.order.length === 0) return false;
    const guid = this.guid(id);
    const template = this.ref(guid);
    return template !== this.baseRef(guid) || (template !== undefined && edit.data.has(template));
  }

  /**
   * The node's data without its own edits: the container's, or for an instance whose template is edited, the
   * instance over the template's data as it is now (kept until the next edit).
   */
  private view(id: number): NodeObject | undefined {
    if (!this.overEditedTemplate(id)) return this.original(id);
    const views = this.edit!.views;
    const cached = views.get(id);
    if (cached?.revision === this.editRevision) return cached.object;
    const object = this.buildNode(id, true);
    views.set(id, { revision: this.editRevision, object });
    return object;
  }

  /** The node's data: its edited data, or as it is without edits of its own (view). */
  private plain(id: number): NodeObject | undefined {
    return this.edit?.data.get(id) ?? this.view(id);
  }

  /** The node's data to read and write through node(): a copy of what it is without edits, made when first used. */
  private nodeData(id: number): NodeObject {
    const edit = this.edits();
    let data = edit.data.get(id);
    if (!data) edit.data.set(id, (data = copyValue(this.view(id) ?? {})));
    return data!;
  }

  /** Write one field of a node (a write through node()). */
  private writeField(id: number, key: string, value: unknown, remove: boolean) {
    const data = this.nodeData(id);
    // only the indexes of the field written change
    this.reindex(id, false, key);
    if (remove) delete data[key];
    else data[key] = value;
    this.edits().changed.add(id);
    this.editRevision++;
    this.reindex(id, true, key);
  }

  /**
   * node(id): the node's data, decoded when first read. Reads come from the container's decode until something could
   * change it: a write, or reading an object or array (which can be changed in place) makes the node's own copy.
   */
  private nodeProxy(id: number): NodeObject {
    const graph = this;
    const data = () => graph.nodeData(id);
    const read = () => graph.plain(id) ?? data();
    return new Proxy({} as NodeObject, {
      get(_target, key) {
        if (key === "toJSON") return () => copyValue(graph.plain(id) ?? {});
        if (typeof key === "symbol") return undefined;
        const value = read()[key];
        return value !== null && typeof value === "object" ? data()[key] : value;
      },
      set(_target, key, value) {
        if (typeof key === "symbol") return false;
        graph.writeField(id, key, value, false);
        return true;
      },
      deleteProperty(_target, key) {
        if (typeof key === "symbol") return false;
        graph.writeField(id, key, undefined, true);
        return true;
      },
      defineProperty(_target, key, descriptor) {
        if (typeof key === "symbol") return false;
        graph.writeField(id, key, descriptor.value, false);
        return true;
      },
      has(_target, key) {
        return typeof key === "string" && key in read();
      },
      ownKeys() {
        return Reflect.ownKeys(read());
      },
      getOwnPropertyDescriptor(_target, key) {
        if (typeof key === "symbol") return undefined;
        const object = read();
        if (!(key in object)) return undefined;
        const value = object[key];
        // what spread/Object.entries hand out can be changed in place too
        return { value: value !== null && typeof value === "object" ? data()[key] : value, writable: true, enumerable: true, configurable: true };
      },
    });
  }

  /** Keep the field indexes (nodesByField) in step with a node's data: every field's, or one field's. */
  private reindex(id: number, add: boolean, field?: string) {
    const indexes = field === undefined ? [...this.fieldIndexes.values()] : [this.fieldIndexes.get(field)].filter((i) => i !== undefined);
    for (const byNormalizer of indexes) {
      for (const index of byNormalizer!.values()) {
        if (add) {
          if (this.isListed(id)) index.add(id);
        } else index.remove(id);
      }
    }
  }

  /**
   * One field of node(id), without making the node object for the common ones: `hidden`, `id` and rule data come
   * straight from the columns and the release.
   */
  field(id: number, name: string): unknown {
    const data = this.edit?.data.get(id);
    if (data) return data[name];
    if (this.overEditedTemplate(id)) return this.view(id)?.[name];
    if (!this.isListed(id) || this.objects[id] !== UNSET) return this.original(id)?.[name];
    if (name === "hidden") {
      // a release's own rule data says itself whether a node is hidden (as node() gives it)
      if (this.ownRuleData) return this.original(id)?.hidden;
      return this.minimal ? (this.isGlobal(id) ? undefined : false) : this.isHidden(id);
    }
    if (!this.isGlobal(id) && (name === "path" || name === "index" || name === "parent_path") && !this.propertyOffsets) {
      // an instance's pathing comes from its path (as buildNode makes it), unless its template says otherwise
      const template = this.release?.ruleNode(this.guid(id));
      if (template?.[name] !== undefined && name !== "path") return template[name];
      const path = this.deref(id);
      if (name === "path") return path;
      const parts = path.split("/");
      if (name === "index") return parts.length >= 3 ? parts[parts.length - 2] : undefined;
      const attributeParts = parts.slice(0, -1);
      return attributeParts.length > 2 ? attributeParts.slice(0, -2).join("/") || undefined : undefined;
    }
    if (name === "id" && !this.isGlobal(id)) return this.guid(id);
    if (!this.propertyOffsets && !OWN_FIELDS.has(name) && name !== "id" && this.release) return this.release.ruleNode(this.guid(id))?.[name];
    return this.original(id)?.[name];
  }

  // ---- lookups ----

  /**
   * One node by key: its path or guid, or a node whose `path` or `id` is the key, or one whose publicId or description
   * is; or, for `<path>/<attribute>`, an attribute (by id, publicId or description) whose own path is
   * `<path>/<its id>`. The first such node in graph order. With `traverse`, a key not found is tried again a level
   * up its path at a time (`employees/1/roles/2/attr`, then `employees/1/attr`, then `attr`): the closest match.
   */
  find(key: string, options: { traverse?: boolean } = {}): number | undefined {
    let found = this.findOne(key);
    if (found !== undefined || !options.traverse || !key.includes("/")) return found;
    const parts = key.split("/");
    const attribute = parts.pop();
    if (!attribute) return undefined;
    while (parts.length >= 2) {
      parts.pop();
      parts.pop();
      const path = parts.join("/");
      found = this.findOne(path ? `${path}/${attribute}` : attribute);
      if (found !== undefined) return found;
    }
    return this.findOne(attribute);
  }

  private findOne(key: string): number | undefined {
    const id = this.ref(key);
    if (id !== undefined) return this.isListed(id) ? id : undefined;
    const byPath = this.nodesByField("path", key)[0] ?? this.nodesByField("id", key)[0];
    if (byPath !== undefined) return byPath;
    if (isUuid(key)) return undefined;

    const candidates = new Set(this.nodesByFields([
      { field: "publicId", value: key },
      { field: "description", value: key },
    ]));
    const cut = key.lastIndexOf("/");
    if (cut >= 0) {
      const path = key.slice(0, cut);
      const attribute = key.slice(cut + 1);
      for (const other of this.nodesByFields([
        { field: "id", value: attribute },
        { field: "publicId", value: attribute },
        { field: "description", value: attribute },
      ])) {
        if (this.field(other, "path") === `${path}/${this.field(other, "id")}`) candidates.add(other);
      }
    }
    return candidates.size === 0 ? undefined : Math.min(...candidates);
  }

  // ---- per-template views (of the container's nodes, made once per graph) ----

  private firstOfTemplate?: { revision: number; first: Int32Array };
  private templateFieldValues?: { revision: number; values: Map<string, unknown[]> };
  private instancesByTemplate?: { revision: number; offsets: Uint32Array; ids: Uint32Array; templates: Map<string, number> };

  /**
   * Each listed node in order, as ids() has them; without edits, 0..listedCount without making the list. (The
   * per-template views below are made over these, again after edits.)
   */
  private forEachListed(visit: (id: number) => void) {
    if (!this.edit || this.packed) for (let id = 0; id < this.listedCount; id++) visit(id);
    else for (const id of this.ids()) visit(id);
  }

  /** For each template (rule graph node), the first listed node made from it, or -1. */
  templateNodes(): Int32Array {
    if (this.firstOfTemplate?.revision !== this.editRevision) {
      const first = new Int32Array(this.templates.length).fill(-1);
      // a template's global copy first (it carries the rule data unchanged), otherwise its first instance
      this.forEachListed((id) => {
        const t = this.template(id);
        if (t >= 0 && (first[t] < 0 || (this.isGlobal(id) && !this.isGlobal(first[t])))) first[t] = id;
      });
      this.firstOfTemplate = { revision: this.editRevision, first };
    }
    return this.firstOfTemplate.first;
  }

  /** A rule data field (e.g. identifier, validation, fk, entity) for every template, read once per graph (and edit). */
  templateField(name: string): unknown[] {
    if (this.templateFieldValues?.revision !== this.editRevision) this.templateFieldValues = { revision: this.editRevision, values: new Map() };
    let values = this.templateFieldValues.values.get(name);
    if (!values) {
      values = Array.from(this.templateNodes(), (id) => (id < 0 ? undefined : this.field(id, name)));
      this.templateFieldValues.values.set(name, values);
    }
    return values;
  }

  /** The listed instance nodes (not global copies) made from the template with this guid. */
  instancesOf(guid: string): Uint32Array {
    if (this.instancesByTemplate?.revision !== this.editRevision) {
      const count = this.templates.length;
      const offsets = new Uint32Array(count + 1);
      this.forEachListed((id) => { if (!this.isGlobal(id) && this.template(id) >= 0) offsets[this.template(id) + 1]++; });
      for (let t = 0; t < count; t++) offsets[t + 1] += offsets[t];
      const ids = new Uint32Array(offsets[count]);
      const fill = offsets.slice(0, count);
      this.forEachListed((id) => { if (!this.isGlobal(id) && this.template(id) >= 0) ids[fill[this.template(id)]++] = id; });
      const templates = this.instancesByTemplate?.templates ?? new Map(this.templates.map((guid, t) => [guid, t]));
      this.instancesByTemplate = { revision: this.editRevision, offsets, ids, templates };
    }
    const index = this.instancesByTemplate;
    const template = index.templates.get(guid);
    if (template === undefined) {
      // a guid only nodes added since have
      if (this.packed) return new Uint32Array(0);
      return Uint32Array.from(this.ids().filter((id) => !this.isGlobal(id) && this.guid(id) === guid));
    }
    return index.ids.subarray(index.offsets[template], index.offsets[template + 1]);
  }

  /**
   * The identifier attribute node of an entity instance (e.g. `employees/1`), or undefined: the node of an
   * `identifier` template of the instance's entity at that instance.
   */
  identifierNode(instancePath: string): number | undefined {
    const parts = instancePath.split("/");
    if (parts.length < 2) return undefined;
    const entity = parts[parts.length - 2];
    const identifiers = this.templateField("identifier");
    const entities = this.templateField("entity");
    for (let t = 0; t < identifiers.length; t++) {
      if (!identifiers[t] || entities[t] !== entity) continue;
      const id = this.ref(`${instancePath}/${this.templates[t]}`);
      if (id !== undefined && this.isListed(id)) return id;
    }
    // New templates aren't in the container's template table yet.
    for (const added of this.edit?.order ?? []) {
      if (!this.isListed(added) || !this.field(added, "identifier") || this.field(added, "entity") !== entity) continue;
      const id = this.ref(`${instancePath}/${this.guid(added)}`);
      if (id !== undefined && this.isListed(id)) return id;
    }
    return undefined;
  }

  /**
   * A node's value, as getNodeResult(node(id)) has it, without making the node object: its own input or derived
   * value, an instance's falling back to its template's (as its node object merges them).
   */
  result(id: number): unknown {
    const own = this.values(id);
    let input = own?.input;
    let derived = own?.derived;
    if (!this.isGlobal(id) && (input === undefined || derived === undefined)) {
      // the template's global copy, when there is one (templateNodes prefers it)
      const template = this.templateNodes()[this.template(id)];
      const inherited = template >= 0 && this.isGlobal(template) ? this.values(template) : undefined;
      if (input === undefined) input = inherited?.input;
      if (derived === undefined) derived = inherited?.derived;
    }
    return input === undefined ? derived : input;
  }

  /**
   * The nodes within `depth` steps of `from` along successors or predecessors (not including `from`), in the order
   * they're reached; `include` limits the walk to the nodes it accepts.
   */
  neighbourhood(from: number, depth: number, direction: "successors" | "predecessors", include?: (id: number) => boolean): number[] {
    const seen = new Set<number>([from]);
    const found: number[] = [];
    let current = [from];
    for (let level = 0; level < depth && current.length > 0; level++) {
      const next: number[] = [];
      for (const id of current) {
        const neighbours = direction === "successors" ? this.successors(id) : this.predecessors(id);
        for (const to of neighbours) {
          if (seen.has(to) || (include && !include(to))) continue;
          seen.add(to);
          found.push(to);
          next.push(to);
        }
      }
      current = next;
    }
    return found;
  }

  /**
   * Legacy JSON of just these nodes and the edges between them, for renderers that lay out what they draw. Nodes
   * without values of their own (edge-only targets) are left out.
   */
  subgraph(ids: Iterable<number>): GraphJson {
    const included = new Set<number>();
    for (const id of ids) if (this.isListed(id)) included.add(id);
    const nodes: GraphJson["nodes"] = [];
    const edges: GraphJson["edges"] = [];
    for (const id of included) {
      nodes.push({ v: this.deref(id), value: this.node(id) });
      for (const to of this.successors(id)) if (included.has(to)) edges.push({ v: this.deref(id), w: this.deref(to) });
    }
    return {
      options: { directed: true, multigraph: false, compound: false, ...(this.options ?? {}) },
      nodes,
      edges,
    };
  }

  // ---- edges ----

  /**
   * The nodes this node depends on, as the legacy graph's edges: every edge the solve resolved, plus a
   * template-to-template edge for each instance edge (also through edits: while an instance edge is there, its
   * templates' edge is). (The release's own edges are in edgeArrays.)
   */
  successors(id: number): Uint32Array {
    return this.neighbours(id, true);
  }

  /** The nodes that depend on this node (the reverse of successors). */
  predecessors(id: number): Uint32Array {
    return this.neighbours(id, false);
  }

  private neighbours(id: number, out: boolean): Uint32Array {
    const edit = this.edit;
    if (edit ? !this.exists(id) : id < 0 || id >= this.size) return new Uint32Array(0);
    if (id >= this.size) {
      const added = out ? [...(edit!.edgesAdded.get(id)?.keys() ?? [])] : [...(edit!.edgesAddedIn.get(id) ?? [])];
      const found = added.filter((other) => !edit!.removed.has(other));
      return Uint32Array.from(this.withImplied(id, out, found));
    }
    this.buildAdjacency();
    const base = out
      ? this.outTargets!.subarray(this.outOffsets![id], this.outOffsets![id + 1])
      : this.inSources!.subarray(this.inOffsets![id], this.inOffsets![id + 1]);
    if (this.packed) return base;
    const found: number[] = [];
    for (const other of base) {
      if (edit!.removed.has(other)) continue;
      const [from, to] = out ? [id, other] : [other, id];
      if (this.impliedPairs!.has(from * this.size + to)) {
        if (this.implies(from, to)) found.push(other);
      } else if (!edit!.edgesRemoved.get(from)?.has(to)) found.push(other);
    }
    const added = out ? edit!.edgesAdded.get(id)?.keys() : edit!.edgesAddedIn.get(id);
    for (const other of added ?? []) if (!edit!.removed.has(other) && !found.includes(other)) found.push(other);
    return Uint32Array.from(this.withImplied(id, out, found));
  }

  /** A node's neighbours with the template edges edits imply added (after the others). */
  private withImplied(id: number, out: boolean, found: number[]): number[] {
    if (!this.isGlobal(id) || this.ref(this.guid(id)) !== id) return found;
    const counts = this.impliedCounts();
    for (const [t, count] of (out ? counts.out : counts.in).get(this.templateIndex(this.guid(id))) ?? []) {
      if (count === 0) continue;
      const other = this.ref(this.templateGuid(t));
      if (other !== undefined && !found.includes(other)) found.push(other);
    }
    return found;
  }

  /**
   * Whether an edge between two global nodes is implied by edges between their instances: each the template node of
   * its guid, with an edge (not between global nodes) from one's instances to the other's. Of an edited graph.
   */
  private implies(from: number, to: number): boolean {
    if (!this.isGlobal(from) || !this.isGlobal(to)) return false;
    const fromGuid = this.guid(from), toGuid = this.guid(to);
    if (this.ref(fromGuid) !== from || this.ref(toGuid) !== to) return false;
    return (this.impliedCounts().out.get(this.templateIndex(fromGuid))?.get(this.templateIndex(toGuid)) ?? 0) > 0;
  }

  /** Every template edge edits imply, as [from, to] (impliedCounts with both template nodes there). */
  private *impliedByEdits(): Generator<[number, number]> {
    for (const [a, targets] of this.impliedCounts().out) {
      const from = this.ref(this.templateGuid(a));
      if (from === undefined) continue;
      for (const [b, count] of targets) {
        const to = count > 0 ? this.ref(this.templateGuid(b)) : undefined;
        if (to !== undefined) yield [from, to];
      }
    }
  }

  /** The edges between instances per template pair (DecisionGraphEdits.implied): counted from the graph as it is. */
  private impliedCounts(): NonNullable<DecisionGraphEdits["implied"]> {
    const edit = this.edits();
    if (edit.implied) return edit.implied;
    const counts: NonNullable<DecisionGraphEdits["implied"]> = { out: new Map(), in: new Map() };
    edit.implied = counts;
    const offsets = this.edgeOffsets, targets = this.edgeTargets, sources = this.edgeSources;
    for (let from = 0; from < this.size; from++) {
      if (edit.removed.has(from)) continue;
      const removed = edit.edgesRemoved.get(from);
      for (let p = offsets[from]; p < offsets[from + 1]; p++) {
        const to = targets[p];
        if (!(sources[p] & EDGE_FROM_STATE) || edit.removed.has(to) || removed?.has(to)) continue;
        this.countImplied(from, to, 1);
      }
    }
    for (const [from, tos] of edit.edgesAdded) {
      if (edit.removed.has(from)) continue;
      for (const to of tos.keys()) if (!edit.removed.has(to)) this.countImplied(from, to, 1);
    }
    return counts;
  }

  /** Count an edge added (1) or removed (-1) towards the template edge it implies, once they're being counted. */
  private countImplied(from: number, to: number, change: 1 | -1) {
    const counts = this.edit?.implied;
    if (!counts || (this.isGlobal(from) && this.isGlobal(to))) return;
    const a = this.templateIndex(this.guid(from)), b = this.templateIndex(this.guid(to));
    for (const [map, key, other] of [[counts.out, a, b], [counts.in, b, a]] as const) {
      let row = map.get(key);
      if (!row) map.set(key, (row = new Map()));
      row.set(other, (row.get(other) ?? 0) + change);
    }
  }

  /** Whether the container has this edge as a solved edge (not one only an instance edge implies). */
  private baseSolved(from: number, to: number): boolean {
    if (from >= this.size || to >= this.size || this.impliedPairs!.has(from * this.size + to)) return false;
    for (let p = this.outOffsets![from]; p < this.outOffsets![from + 1]; p++) if (this.outTargets![p] === to) return true;
    return false;
  }

  /** Whether there's an edge from one node to another. */
  hasEdge(from: number, to: number): boolean {
    const edit = this.edit;
    if (edit ? !this.exists(from) || !this.exists(to) : from >= this.size || to >= this.size) return false;
    if (from >= this.size || to >= this.size) {
      return !!edit?.edgesAdded.get(from)?.has(to) || this.implies(from, to);
    }
    this.buildAdjacency();
    if (!edit || this.packed) {
      for (let p = this.outOffsets![from]; p < this.outOffsets![from + 1]; p++) if (this.outTargets![p] === to) return true;
      return false;
    }
    if (edit.edgesAdded.get(from)?.has(to)) return true;
    if (!edit.edgesRemoved.get(from)?.has(to) && this.baseSolved(from, to)) return true;
    return this.implies(from, to);
  }

  /** An edge's label (the rule graph edge's value), or undefined. */
  edge(from: number, to: number): unknown {
    const edit = this.edit;
    const added = edit?.edgesAdded.get(from);
    if (added?.has(to)) return added.get(to);
    const relabelled = edit?.edgeLabels.get(from);
    if (relabelled?.has(to)) return relabelled.get(to);
    if (!this.ruleEdgeLabels) {
      this.ruleEdgeLabels = new Map();
      if (this.sectionView("ruleEdgeData", Uint8Array)) {
        for (const record of this.ruleEdgeRecords()) {
          if (record.value === undefined) continue;
          const v = this.baseRef(record.v), w = this.baseRef(record.w);
          if (v === undefined || w === undefined) continue;
          let to = this.ruleEdgeLabels.get(v);
          if (!to) this.ruleEdgeLabels.set(v, (to = new Map()));
          to.set(w, record.value);
        }
      }
    }
    return this.hasEdge(from, to) ? this.ruleEdgeLabels.get(from)?.get(to) : undefined;
  }

  /**
   * Add an edge (or change its label, when given one). An edge between instances (or an instance and a global node)
   * also gives their templates' global nodes an edge, as the container's graphs have it.
   */
  setEdge(from: number, to: number, label?: unknown): this {
    if (!this.exists(from) || !this.exists(to)) throw new RangeError(`No decision graph node ${this.exists(from) ? to : from}`);
    const edit = this.edits();
    const relabel = arguments.length > 2;
    if (this.hasEdge(from, to)) {
      if (!relabel) return this;
      const added = edit.edgesAdded.get(from);
      if (added?.has(to)) added.set(to, label);
      else {
        let labels = edit.edgeLabels.get(from);
        if (!labels) edit.edgeLabels.set(from, (labels = new Map()));
        labels.set(to, label);
      }
      edit.shape++;
      this.editRevision++;
      return this;
    }
    // a removed edge set again goes after the node's other edges, as a new one does (graphlib's order): it stays
    // removed from the container's and is added
    let added = edit.edgesAdded.get(from);
    if (!added) edit.edgesAdded.set(from, (added = new Map()));
    added.set(to, label);
    let into = edit.edgesAddedIn.get(to);
    if (!into) edit.edgesAddedIn.set(to, (into = new Set()));
    into.add(from);
    edit.edgeLog.push(from, to);
    this.countImplied(from, to, 1);
    edit.shape++;
    this.editRevision++;
    return this;
  }

  /**
   * Remove an edge. A template edge that an edge between their instances implies stays while that edge does (as it
   * would come back when the graph is written and read): remove the instance edges.
   */
  removeEdge(from: number, to: number): this {
    const edit = this.edits();
    const added = edit.edgesAdded.get(from);
    if (added?.delete(to)) {
      edit.edgesAddedIn.get(to)?.delete(from);
      this.countImplied(from, to, -1);
      edit.shape++;
      this.editRevision++;
      return this;
    }
    if (!this.exists(from) || !this.exists(to) || from >= this.size || to >= this.size) return this;
    this.buildAdjacency();
    if (edit.edgesRemoved.get(from)?.has(to) || !this.baseSolved(from, to)) return this;
    let removed = edit.edgesRemoved.get(from);
    if (!removed) edit.edgesRemoved.set(from, (removed = new Set()));
    removed.add(to);
    edit.edgeLabels.get(from)?.delete(to);
    this.countImplied(from, to, -1);
    edit.shape++;
    this.editRevision++;
    return this;
  }

  /**
   * How many edges each node reachable from `from` is from it, along successors (or predecessors): a breadth-first
   * walk, so the shortest. Nodes not reached aren't in the map.
   */
  distances(from: number, direction: "successors" | "predecessors" = "successors"): Map<number, number> {
    const reached = new Map<number, number>([[from, 0]]);
    const queue = [from];
    for (let head = 0; head < queue.length; head++) {
      const id = queue[head];
      const next = direction === "successors" ? this.successors(id) : this.predecessors(id);
      for (const to of next) {
        if (reached.has(to)) continue;
        reached.set(to, reached.get(id)! + 1);
        queue.push(to);
      }
    }
    return reached;
  }

  /** The nodes reachable from `from` along successors, depth first, each before its successors. */
  preorder(from: number): number[] {
    const seen = new Set<number>();
    const order: number[] = [];
    const stack = [from];
    while (stack.length > 0) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      order.push(id);
      const next = this.successors(id);
      for (let i = next.length - 1; i >= 0; i--) if (!seen.has(next[i])) stack.push(next[i]);
    }
    return order;
  }

  /** 1 for every node reachable from `from` along successors (including `from`), indexed by id (idBound() long). */
  reachable(from: number): Uint8Array {
    this.buildAdjacency();
    if (!this.packed) {
      const reached = new Uint8Array(this.idBound());
      if (!this.exists(from)) return reached;
      const queue = [from];
      reached[from] = 1;
      for (let head = 0; head < queue.length; head++) {
        for (const to of this.successors(queue[head])) {
          if (!reached[to]) {
            reached[to] = 1;
            queue.push(to);
          }
        }
      }
      return reached;
    }
    const reached = new Uint8Array(this.size);
    if (from >= this.size) return reached;
    const queue = new Uint32Array(this.size);
    let tail = 0;
    reached[from] = 1;
    queue[tail++] = from;
    for (let head = 0; head < tail; head++) {
      const id = queue[head];
      for (let p = this.outOffsets![id]; p < this.outOffsets![id + 1]; p++) {
        const to = this.outTargets![p];
        if (!reached[to]) {
          reached[to] = 1;
          queue[tail++] = to;
        }
      }
    }
    return reached;
  }

  /**
   * The engine's edges as CSR arrays (views over the container), including release edges, with their sources. A graph
   * with edits throws: call toPacked() first.
   */
  edgeArrays(): { offsets: Uint32Array; targets: Uint32Array; sources: Uint8Array } {
    this.assertPacked("edgeArrays");
    return { offsets: this.edgeOffsets, targets: this.edgeTargets, sources: this.edgeSources };
  }

  /**
   * The engine's edges as [from, to, sources] (sources: EDGE_FROM_RELEASE | EDGE_FROM_STATE). A graph with edits throws:
   * call toPacked() first.
   */
  edgeEntries(): Generator<[number, number, number]> {
    this.assertPacked("edgeEntries");
    return this.containerEdgeEntries();
  }

  /** Container-level reads (properties, edgeArrays, edgeEntries) would miss edits: they need toPacked(). */
  private assertPacked(method: string) {
    if (this.edited()) throw new Error(`DecisionGraph.${method}() reads the container, and this graph has edits: call toPacked() first`);
  }

  private *containerEdgeEntries(): Generator<[number, number, number]> {
    for (let from = 0; from < this.size; from++) {
      for (let p = this.edgeOffsets[from]; p < this.edgeOffsets[from + 1]; p++) {
        yield [from, this.edgeTargets[p], this.edgeSources[p]];
      }
    }
  }

  /** The legacy graph's label (its instancePaths is the compressed-edge table), made on first read. */
  /** Replace the graph's label (the rule graph's value). */
  setLabel(value: unknown): this {
    this.edits().label = { value };
    this.editRevision++;
    return this;
  }

  label(): Record<string, unknown> {
    if (this.edit?.label) return this.edit.label.value as Record<string, unknown>;
    if (this.hasRuleData || this.header.ruleGraphValue !== undefined) return this.ruleGraphLabel() as Record<string, unknown>;
    if (!this.graphLabel) {
      const instancePaths: string[] = [];
      const seen = new Set<number>();
      const intern = (id: number) => {
        const instance = this.instance(id);
        if (instance >= 0 && !seen.has(instance)) {
          seen.add(instance);
          instancePaths.push(this.instancePathOf(instance));
        }
      };
      for (const [from, to, sources] of this.containerEdgeEntries()) {
        if (!(sources & EDGE_FROM_STATE) || (this.isGlobal(from) && this.isGlobal(to))) continue;
        intern(from);
        intern(to);
      }
      const id = this.labelId ?? this.id;
      this.graphLabel = {
        ...(id !== undefined ? { id } : {}),
        instancePaths,
        debug: true,
        ...(this.minimal
          ? { minimal: true, debugInstancesUnhydrated: true }
          : { debugInstancesUnhydrated: true, pathingUnhydrated: true }),
      };
    }
    return this.graphLabel;
  }

  // ---- internals ----

  private instancePathOf(instance: number): string {
    if (!this.instancePaths) this.instancePaths = new Array(this.instanceParent.length);
    let path = this.instancePaths[instance];
    if (path === undefined) {
      const parent = this.instanceParent[instance];
      const own = `${this.header.entityNames[this.instanceEntity[instance]]}/${this.instanceIndexOf(instance)}`;
      path = parent < 0 ? own : `${this.instancePathOf(parent)}/${own}`;
      this.instancePaths[instance] = path;
    }
    return path;
  }

  private instanceIndexOf(instance: number): string | number {
    const index = this.instanceIndex[instance];
    return index > 0 ? index : this.header.externalIds[-index - 1];
  }

  private reasonOf(reason: number): DecisionGraphReason | undefined {
    if (reason === 0) return undefined;
    if (!this.reasons) this.reasons = new Array(this.reasonOffsets.length - 1);
    return (this.reasons[reason - 1] ??= unpackr.unpack(
      this.reasonData.subarray(this.reasonOffsets[reason - 1], this.reasonOffsets[reason]),
    ) as DecisionGraphReason);
  }

  private buildAdjacency() {
    if (this.outOffsets) return;
    const size = this.size;
    const templateNode = new Int32Array(this.templates.length).fill(-1);
    for (let id = 0; id < size; id++) if (this.isGlobal(id)) templateNode[this.template(id)] = id;

    // Solved edges are already unique (the engine keeps one per pair). Their template pairs are few: collect
    // those, minus any that are solved edges themselves.
    const offsets = this.edgeOffsets;
    const targets = this.edgeTargets;
    const sources = this.edgeSources;
    const solvedGlobal = new Set<number>();
    const templatePairs = new Set<number>();
    let solved = 0;
    for (let from = 0; from < size; from++) {
      const fromGlobal = this.isGlobal(from);
      for (let p = offsets[from]; p < offsets[from + 1]; p++) {
        if (!(sources[p] & EDGE_FROM_STATE)) continue;
        solved++;
        const to = targets[p];
        if (fromGlobal && this.isGlobal(to)) {
          solvedGlobal.add(from * size + to);
        } else {
          const a = templateNode[this.template(from)];
          const b = templateNode[this.template(to)];
          if (a >= 0 && b >= 0) templatePairs.add(a * size + b);
        }
      }
    }
    for (const key of solvedGlobal) templatePairs.delete(key);
    this.impliedPairs = templatePairs;

    const count = solved + templatePairs.size;
    const froms = new Uint32Array(count);
    const tos = new Uint32Array(count);
    let n = 0;
    for (let from = 0; from < size; from++) {
      for (let p = offsets[from]; p < offsets[from + 1]; p++) {
        if (!(sources[p] & EDGE_FROM_STATE)) continue;
        froms[n] = from;
        tos[n++] = targets[p];
      }
    }
    for (const key of templatePairs) {
      froms[n] = Math.floor(key / size);
      tos[n++] = key % size;
    }

    const out = new Uint32Array(size + 1);
    const inn = new Uint32Array(size + 1);
    for (let p = 0; p < count; p++) {
      out[froms[p] + 1]++;
      inn[tos[p] + 1]++;
    }
    for (let id = 0; id < size; id++) {
      out[id + 1] += out[id];
      inn[id + 1] += inn[id];
    }
    const outTargets = new Uint32Array(count);
    const inSources = new Uint32Array(count);
    const outFill = out.slice(0, size);
    const inFill = inn.slice(0, size);
    for (let p = 0; p < count; p++) {
      outTargets[outFill[froms[p]]++] = tos[p];
      inSources[inFill[tos[p]]++] = froms[p];
    }
    this.orderByRuleEdges(out, outTargets, inn, inSources);
    this.outOffsets = out;
    this.outTargets = outTargets;
    this.inOffsets = inn;
    this.inSources = inSources;
  }

  /**
   * A graph that carries its rule graph's edges (a release's, or a legacy graph converted with its rule records) lists
   * each node's successors and predecessors in those edges' order, as the rule graph (and graphlib) had them: the order
   * rules reference their children in, which reports and the engine follow. An instance edge takes its templates'
   * edge's place. Edges the rule graph doesn't have come after, in node order.
   */
  private orderByRuleEdges(out: Uint32Array, outTargets: Uint32Array, inn: Uint32Array, inSources: Uint32Array) {
    const ruleEdges = this.sectionView("ruleEdges", Uint32Array);
    if (!ruleEdges || ruleEdges.length === 0) return;
    const templates = this.templates.length;
    const rank = new Map<number, number>();
    for (let i = 0; i < ruleEdges.length / 2; i++) {
      const from = ruleEdges[i * 2], to = ruleEdges[i * 2 + 1];
      if (from >= this.size || to >= this.size) continue;
      const key = this.nodeTemplate[from] * templates + this.nodeTemplate[to];
      if (!rank.has(key)) rank.set(key, i);
    }
    const rankOf = (from: number, to: number) => rank.get(this.nodeTemplate[from] * templates + this.nodeTemplate[to]) ?? Number.MAX_SAFE_INTEGER;
    this.edgeRank = rankOf;
    const sortSlice = (offsets: Uint32Array, ids: Uint32Array, key: (node: number, other: number) => number) => {
      for (let node = 0; node < this.size; node++) {
        const start = offsets[node], end = offsets[node + 1];
        if (end - start < 2) continue;
        const slice = Array.from(ids.subarray(start, end));
        slice.sort((a, b) => key(node, a) - key(node, b) || a - b);
        ids.set(slice, start);
      }
    };
    sortSlice(out, outTargets, (from, to) => rankOf(from, to));
    sortSlice(inn, inSources, (to, from) => rankOf(from, to));
  }

  /** A global node as the legacy graph had it: rule data (full graph), values, hidden (full graph), debug reason. */
  private globalNode(id: number): NodeObject {
    const values = this.baseValues(id);
    const ruleNode = this.release?.ruleNode(this.guid(id));
    const object: NodeObject = { ...(ruleNode ?? {}), ...this.containerProperties(id) };
    if (values?.input !== undefined) object.input = values.input;
    if (values?.derived !== undefined) object.derived = values.derived;
    // a solved graph's global copy of an entity attribute is hidden; a release's own rule data says so itself
    if (!this.minimal && !this.ownRuleData) object.hidden = this.isHidden(id);
    if (values?.justification !== undefined) object.justification = values.justification;
    if (values?.triggered !== undefined) object.triggered = values.triggered;
    const documents = this.release?.documents;
    if (documents && ruleNode) {
      // definedIn/usedIn as document names, as the engine did for debug graphs
      if (typeof ruleNode.definedIn === "string") {
        const definedIn = documentRef(documents, ruleNode.definedIn, false);
        if (definedIn !== undefined) object.definedIn = definedIn;
      }
      if (Array.isArray(ruleNode.usedIn)) {
        object.usedIn = ruleNode.usedIn
          .map((item: string) => documentRef(documents, item, false))
          .filter((item: string | undefined) => item !== undefined);
      }
    }
    if (values?.reason) object.debugInfo = { reason: values.reason };
    return object;
  }

  /**
   * The node object of a container node: over its template's as the container has it, or (`current`) as it is now.
   */
  private buildNode(id: number, current: boolean): NodeObject | undefined {
    // nodes only reached as edge targets had no value (graphlib made them from the edge)
    if (id >= this.listedCount) return undefined;
    if (this.isGlobal(id)) return this.globalNode(id);

    // an instance: its own values over its template's (the global copy), with pathing from its path
    const values = this.baseValues(id);
    const own: NodeObject = {};
    if (values?.input !== undefined) own.input = values.input;
    if (values?.derived !== undefined) own.derived = values.derived;
    if (values?.justification !== undefined) own.justification = values.justification;
    if (values?.triggered !== undefined) own.triggered = values.triggered;
    if (values?.reason) own.debugInfo = { reason: values.reason };

    const guid = this.guid(id);
    const templateId = current ? this.ref(guid) : this.baseRef(guid);
    const template = templateId === undefined ? this.globalNode(id) : current ? this.plain(templateId) : this.original(templateId);
    const path = this.deref(id);
    const parts = path.split("/");
    const attributeParts = parts.slice(0, -1);
    return removeUndefinedFields({
      ...template,
      ...this.containerProperties(id),
      ...own,
      id: guid,
      hidden: own.hidden ?? false,
      index: own.index ?? template?.index ?? (parts.length >= 3 ? parts[parts.length - 2] : undefined),
      path: own.path ?? path,
      parent_path: own.parent_path ?? (attributeParts.length > 2 ? attributeParts.slice(0, -2).join("/") || undefined : undefined),
    });
  }
}

// node(id) fields that come from the node's own values or path, not the rule data
const OWN_FIELDS = new Set(["input", "derived", "justification", "triggered", "debugInfo", "index", "path", "parent_path", "definedIn", "usedIn"]);

const RESERVED_PROPERTIES = new Set([...OWN_FIELDS, "id", "hidden"]);

export const isDecisionGraph = (graph: unknown): graph is DecisionGraph => graph instanceof DecisionGraph;

/** What a decision graph is written from (writeDecisionGraph): the engine's layout, in JS. */
export type DecisionGraphParts = {
  id?: string;
  session: boolean;
  debug: boolean;
  options?: Record<string, unknown>;
  templates: string[];
  templateEntity: Uint8Array;
  /** Per instance: parent instance (-1), entity name, and its index (1-based position, or an external id). */
  instances: { parent: number; entity: string; index: number | string }[];
  /** Per node, `nodeCount` of them first, then nodes only reached as edge targets. */
  nodes: { template: number; instance: number }[];
  nodeCount: number;
  values: (DecisionGraphNodeValues | null | undefined)[];
  /** Opaque MessagePack persistent state per node, including edge-only nodes. */
  nodeStates?: (Uint8Array | undefined)[];
  /** Validation references use the same node IDs as edges. */
  validations?: { node: number; severity: string; message: string; shown: boolean; attributes: number[] }[];
  /** [from, to, sources] */
  edges: [number, number, number][];
  /**
   * Per listed node, its rule graph node's value (a release's compiled graph carries them: `ruleData`), so the rule
   * graph's nodes come back exactly as they were, in order.
   */
  ruleNodes?: (Record<string, unknown> | undefined)[];
  /** Sparse extra properties per listed node; omitted/undefined keys inherit, null overrides. */
  nodeProperties?: (Record<string, unknown> | undefined)[];
  /** Property names to carry across solves. Unmarked properties are transient. */
  persistentProperties?: string[];
  /**
   * A release's rule graph edges as [from node, to node], in the rule graph's order (`ruleEdges`). The engine builds
   * rules' parents and children in this order; `edges` are kept sorted per node.
   */
  ruleEdges?: [number, number][];
  /** Per rule edge, anything it carries besides its ends (a multigraph's `name`, a `value`): `ruleEdgeData`. */
  ruleEdgeLabels?: (Record<string, unknown> | undefined)[];
  /** Per listed node, anything its rule graph node carries besides `v` and `value`: `ruleNodeExtraData`. */
  ruleNodeExtras?: (Record<string, unknown> | undefined)[];
  /** The rule graph's own label (`value`), in the header. */
  ruleGraphValue?: unknown;
  /** A session's entity structure (persistent state the engine reads back), in the header. */
  entityStructure?: unknown;
};

/**
 * A copy of `value` whose integers beyond 32 bits are BigInts: msgpackr writes those as 64-bit integers, where it would
 * write the numbers as floats. The engine reads rule data integers as integers, as it does from the JSON.
 */
const withExactIntegers = (value: unknown): unknown => {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) && (value > 0xffffffff || value < -0x80000000) ? BigInt(value) : value;
  }
  if (Array.isArray(value)) return value.map(withExactIntegers);
  if (value && typeof value === "object" && !(value instanceof Uint8Array)) {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      const item = (value as Record<string, unknown>)[key];
      if (item !== undefined) out[key] = withExactIntegers(item);
    }
    return out;
  }
  return value;
};

/** A byte string's bytes as a string key (for interning records by their bytes). */
const byteKeyDecoder = new TextDecoder("latin1");
const latin1 = (bytes: Uint8Array) => byteKeyDecoder.decode(bytes);

/** Writes a decision graph container (the same layout the engine writes). */
export const writeDecisionGraph = (parts: DecisionGraphParts): Uint8Array => {
  const intern = <T>(key: (value: T) => string) => {
    const ids = new Map<string, number>();
    const order: T[] = [];
    return {
      order,
      id: (value: T) => {
        const k = key(value);
        let id = ids.get(k);
        if (id === undefined) {
          order.push(value);
          id = order.length;
          ids.set(k, id);
        }
        return id;
      },
    };
  };
  const entityNames = intern<string>((name) => name);
  const externalIds = intern<string>((id) => id);
  const justifications = intern<string>((text) => text);
  const reasons = intern<DecisionGraphReason>((reason) => JSON.stringify(reason));

  const size = parts.nodes.length;
  const nodeReason = new Uint32Array(parts.nodeCount);
  const nodeJustification = new Uint32Array(parts.nodeCount);
  // each distinct value record once (keyed by its bytes), referenced per node
  const nodeValue = new Uint32Array(parts.nodeCount);
  const valueRecordIds = new Map<string, number>();
  const valueChunks: Uint8Array[] = [];
  let valueLength = 0;
  for (let id = 0; id < parts.nodeCount; id++) {
    const values = parts.values[id];
    if (values?.reason) nodeReason[id] = reasons.id(values.reason);
    if (values?.justification !== undefined) nodeJustification[id] = justifications.id(values.justification);
    const record: unknown[] = [0];
    if (values && "input" in values && values.input !== undefined) {
      record[0] = (record[0] as number) | VALUE_INPUT;
      record.push(values.input);
    }
    if (values && "derived" in values && values.derived !== undefined) {
      record[0] = (record[0] as number) | VALUE_DERIVED;
      record.push(values.derived);
    }
    if (values?.triggered !== undefined) {
      record[0] = (record[0] as number) | VALUE_TRIGGERED;
      record.push(values.triggered);
    }
    if (record[0] !== 0) {
      // pack() reuses its buffer, so copy each chunk out
      const chunk = new Uint8Array(packr.pack(record));
      const key = latin1(chunk);
      let recordId = valueRecordIds.get(key);
      if (recordId === undefined) {
        valueChunks.push(chunk);
        valueLength += chunk.length;
        recordId = valueChunks.length;
        valueRecordIds.set(key, recordId);
      }
      nodeValue[id] = recordId;
    }
  }
  const valueOffsets = new Uint32Array(valueChunks.length + 1);
  valueChunks.forEach((chunk, i) => (valueOffsets[i + 1] = valueOffsets[i] + chunk.length));

  // edges: each node's targets grouped by template, then by instance, as differences (see decodeEdges)
  const edgesByNode: [number, number, number][][] = Array.from({ length: size }, () => []);
  for (const [from, to, sources] of parts.edges) {
    edgesByNode[from].push([parts.nodes[to].template, parts.nodes[to].instance, sources]);
  }
  const edgeGroupCounts = new Uint32Array(size);
  const edgeGroupTemplates: number[] = [];
  const edgeGroupSizes: number[] = [];
  const edgeTargetInstances = new Int32Array(parts.edges.length);
  const edgeSources = new Uint8Array(parts.edges.length);
  let edge = 0;
  for (let from = 0; from < size; from++) {
    const targets = edgesByNode[from].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    let previousTemplate = parts.nodes[from].template;
    for (let i = 0; i < targets.length; ) {
      const template = targets[i][0];
      const start = i;
      let previousInstance = parts.nodes[from].instance;
      for (; i < targets.length && targets[i][0] === template; i++, edge++) {
        edgeTargetInstances[edge] = targets[i][1] - previousInstance;
        edgeSources[edge] = targets[i][2];
        previousInstance = targets[i][1];
      }
      edgeGroupTemplates.push(template - previousTemplate);
      edgeGroupSizes.push(i - start);
      previousTemplate = template;
      edgeGroupCounts[from]++;
    }
  }

  const instanceEntity = Uint32Array.from(parts.instances, (instance) => entityNames.id(instance.entity) - 1);
  const instanceIndex = Int32Array.from(parts.instances, (instance) =>
    typeof instance.index === "number" ? instance.index : -externalIds.id(instance.index),
  );

  const reasonChunks = reasons.order.map((reason) => new Uint8Array(packr.pack(reason)));
  const reasonOffsets = new Uint32Array(reasonChunks.length + 1);
  reasonChunks.forEach((chunk, i) => (reasonOffsets[i + 1] = reasonOffsets[i] + chunk.length));

  const concat = (chunks: Uint8Array[], length: number) => {
    const out = new Uint8Array(length);
    let at = 0;
    for (const chunk of chunks) {
      out.set(chunk, at);
      at += chunk.length;
    }
    return out;
  };
  const asBytes = (array: Uint32Array | Int32Array | Uint8Array) =>
    new Uint8Array(array.buffer, array.byteOffset, array.byteLength);
  const sectionData: [string, Uint8Array][] = [
    ["nodeTemplate", asBytes(Uint32Array.from(parts.nodes, (node) => node.template))],
    ["nodeInstance", asBytes(Int32Array.from(parts.nodes, (node) => node.instance))],
    ["nodeReason", asBytes(nodeReason)],
    ["nodeJustification", asBytes(nodeJustification)],
    ["nodeValue", asBytes(nodeValue)],
    ["valueOffsets", asBytes(valueOffsets)],
    ["valueData", concat(valueChunks, valueLength)],
    ["instanceParent", asBytes(Int32Array.from(parts.instances, (instance) => instance.parent))],
    ["instanceEntity", asBytes(instanceEntity)],
    ["instanceIndex", asBytes(instanceIndex)],
    ["edgeGroupCounts", asBytes(edgeGroupCounts)],
    ["edgeGroupTemplates", asBytes(Int32Array.from(edgeGroupTemplates))],
    ["edgeGroupSizes", asBytes(Uint32Array.from(edgeGroupSizes))],
    ["edgeTargetInstances", asBytes(edgeTargetInstances)],
    ["edgeSources", edgeSources],
    ["reasonOffsets", asBytes(reasonOffsets)],
    ["reasonData", concat(reasonChunks, reasonOffsets[reasonChunks.length])],
  ];
  if (parts.nodeProperties) {
    if (parts.nodeProperties.length > parts.nodeCount) throw new Error("Properties provided for an unlisted node");
    const keys = new Map<string, number>();
    const populated: number[] = [];
    const chunks: Uint8Array[] = [];
    let total = 0;
    for (let id = 0; id < parts.nodeProperties.length; id++) {
      const own = parts.nodeProperties[id];
      if (!own) continue;
      const pairs: unknown[] = [];
      for (const [name, value] of Object.entries(own)) {
        if (value === undefined) continue;
        if (RESERVED_PROPERTIES.has(name)) throw new Error(`Reserved node property: ${name}`);
        if (!keys.has(name)) keys.set(name, keys.size);
        pairs.push(keys.get(name), value);
      }
      if (!pairs.length) continue;
      const chunk = new Uint8Array(packr.pack(pairs));
      populated.push(id);
      chunks.push(chunk);
      total += chunk.length;
    }
    if (keys.size) {
      const sparse = populated.length * 8 + 48 < parts.nodeCount * 4;
      const rows = sparse ? populated.length : parts.nodeCount;
      const unique: Uint8Array[] = [];
      const recordIds = new Map<string, number>();
      let uniqueSize = 0;
      // Avoid building a full interning table for predominantly unique annotations.
      const sample = chunks.slice(0, 64);
      const trySharing = new Set(sample.map(latin1)).size < sample.length;
      const refs = trySharing ? chunks.map((chunk) => {
        const key = latin1(chunk);
        let ref = recordIds.get(key);
        if (ref === undefined) {
          unique.push(chunk);
          uniqueSize += chunk.length;
          ref = unique.length;
          recordIds.set(key, ref);
        }
        return ref;
      }) : [];
      // Shared records add one offset per unique record; only pay that cost when it saves bytes.
      const shared = trySharing && uniqueSize + unique.length * 4 + 48 < total;
      const records = shared ? unique : chunks;
      const offsets = new Uint32Array((shared ? unique.length : rows) + 1);
      if (shared) {
        const references = new Uint32Array(rows);
        populated.forEach((id, i) => { references[sparse ? i : id] = refs[i]; });
        sectionData.push(["nodePropertyRecords", asBytes(references)]);
        unique.forEach((chunk, i) => { offsets[i + 1] = offsets[i] + chunk.length; });
      } else {
        let next = 0;
        for (let row = 0; row < rows; row++) {
          const chunk = sparse || populated[next] === row ? chunks[next++] : undefined;
          offsets[row + 1] = offsets[row] + (chunk?.length ?? 0);
        }
      }
      if (sparse) sectionData.push(["propertyNodes", asBytes(Uint32Array.from(populated))]);
      if (parts.persistentProperties?.length) {
        const persistent = new Set(parts.persistentProperties);
        sectionData.push(["propertyFlags", Uint8Array.from([...keys.keys()], (name) => persistent.has(name) ? 1 : 0)]);
      }
      sectionData.push(
        ["propertyKeys", new Uint8Array(packr.pack([...keys.keys()]))],
        ["nodePropertyOffsets", asBytes(offsets)],
        ["nodePropertyData", concat(records, offsets[offsets.length - 1])],
      );
    }
  }
  const indexedRecords = (name: string, items: (Uint8Array | undefined)[]) => {
    const records = intern<Uint8Array>(latin1);
    const refs = Uint32Array.from(items, (item) => item === undefined ? 0 : records.id(item));
    const offsets = new Uint32Array(records.order.length + 1);
    records.order.forEach((chunk, i) => { offsets[i + 1] = offsets[i] + chunk.length; });
    sectionData.push([name === "state" ? "nodeState" : "validationText", asBytes(refs)],
      [`${name}Offsets`, asBytes(offsets)], [`${name}Data`, concat(records.order, offsets[offsets.length - 1])]);
  };
  if (parts.nodeStates) indexedRecords("state", parts.nodeStates);
  if (parts.validations) {
    const attributes: number[] = [];
    for (const validation of parts.validations) {
      let previous = validation.node;
      for (const id of validation.attributes) { attributes.push(id - previous); previous = id; }
    }
    indexedRecords("validationText", parts.validations.map(({ severity, message }) => new Uint8Array(packr.pack([severity, message]))));
    sectionData.push(
      ["validationNodes", asBytes(Uint32Array.from(parts.validations, (v) => v.node))],
      ["validationShown", Uint8Array.from(parts.validations, (v) => v.shown ? 1 : 0)],
      ["validationAttributeCounts", asBytes(Uint32Array.from(parts.validations, (v) => v.attributes.length))],
      ["validationAttributes", asBytes(Int32Array.from(attributes))],
    );
  }
  if (parts.ruleNodes) {
    // each listed node's rule node, empty when it has none
    const ruleChunks = Array.from({ length: parts.nodeCount }, (_, id) => {
      const rule = parts.ruleNodes![id];
      return rule === undefined ? new Uint8Array(0) : new Uint8Array(packr.pack(withExactIntegers(rule)));
    });
    const ruleOffsets = new Uint32Array(ruleChunks.length + 1);
    ruleChunks.forEach((chunk, i) => (ruleOffsets[i + 1] = ruleOffsets[i] + chunk.length));
    sectionData.push(["ruleOffsets", asBytes(ruleOffsets)], ["ruleData", concat(ruleChunks, ruleOffsets[ruleChunks.length])]);
  }
  if (parts.ruleEdges) sectionData.push(["ruleEdges", asBytes(Uint32Array.from(parts.ruleEdges.flat()))]);
  // optional records per rule edge / node (offsets + data), written only when any has one
  const records = (name: string, items: (Record<string, unknown> | undefined)[] | undefined) => {
    if (!items?.some((item) => item !== undefined)) return;
    const chunks = items.map((item) =>
      item === undefined ? new Uint8Array(0) : new Uint8Array(packr.pack(withExactIntegers(item))),
    );
    const offsets = new Uint32Array(chunks.length + 1);
    chunks.forEach((chunk, i) => (offsets[i + 1] = offsets[i] + chunk.length));
    sectionData.push([`${name}Offsets`, asBytes(offsets)], [`${name}Data`, concat(chunks, offsets[chunks.length])]);
  };
  records("ruleEdge", parts.ruleEdgeLabels);
  records("ruleNodeExtra", parts.ruleNodeExtras);
  const sections: Section[] = [];
  let dataLength = 0;
  for (let i = 0; i < sectionData.length; i++) {
    const [name, original] = sectionData[i];
    const { bytes: data, width } = narrowSection(name, original);
    sectionData[i] = [name, data];
    dataLength = Math.ceil(dataLength / 8) * 8;
    sections.push({ name, offset: dataLength, length: data.length, width });
    dataLength += data.length;
  }

  const header = new Uint8Array(
    packr.pack({
      formatVersion: DECISION_GRAPH_FORMAT_VERSION,
      ...(parts.id !== undefined ? { id: parts.id } : {}),
      session: parts.session,
      debug: parts.debug,
      ...(parts.options !== undefined ? { options: parts.options } : {}),
      ...(parts.ruleGraphValue !== undefined ? { ruleGraphValue: withExactIntegers(parts.ruleGraphValue) } : {}),
      nodeCount: parts.nodeCount,
      templates: parts.templates,
      templateEntity: parts.templateEntity,
      entityNames: entityNames.order,
      externalIds: externalIds.order,
      justifications: justifications.order,
      sections: sections.map(sectionEntry),
      ...(parts.entityStructure !== undefined ? { entityStructure: parts.entityStructure } : {}),
    }),
  );
  const dataStart = Math.ceil((8 + header.length) / 8) * 8;
  const out = new Uint8Array(dataStart + dataLength);
  out.set(MAGIC, 0);
  new DataView(out.buffer).setUint32(4, header.length, true);
  out.set(header, 8);
  sectionData.forEach(([, data], i) => out.set(data, dataStart + sections[i].offset));
  return out;
};

/**
 * The templates, instances and nodes of a container being written from paths (`<entity>/<index>/.../<guid>`, or a
 * global guid): `add(path)` adds a node and gives its id. Instance indexes come from the paths (numeric segments
 * become positions, others stay external ids) unless `knownIndex` has the instance's.
 */
const pathTables = (knownIndex?: (instancePath: string) => string | number | undefined) => {
  const templateIds = new Map<string, number>();
  const templates: string[] = [];
  const entityTemplates = new Set<number>();
  const instanceIds = new Map<string, number>();
  const instances: DecisionGraphParts["instances"] = [];
  const nodes: DecisionGraphParts["nodes"] = [];
  const template = (guid: string) => {
    let id = templateIds.get(guid);
    if (id === undefined) {
      id = templates.length;
      templates.push(guid);
      templateIds.set(guid, id);
    }
    return id;
  };
  const instance = (path: string): number => {
    let id = instanceIds.get(path);
    if (id !== undefined) return id;
    const parts = path.split("/");
    const parent = parts.length > 2 ? instance(parts.slice(0, -2).join("/")) : -1;
    const segment = parts[parts.length - 1];
    id = instances.length;
    instances.push({ parent, entity: parts[parts.length - 2], index: knownIndex?.(path) ?? (/^[1-9]\d*$/.test(segment) ? Number(segment) : segment) });
    instanceIds.set(path, id);
    return id;
  };
  const add = (v: string) => {
    const cut = v.lastIndexOf("/");
    const t = template(cut < 0 ? v : v.slice(cut + 1));
    const i = cut < 0 ? -1 : instance(v.slice(0, cut));
    if (i >= 0) entityTemplates.add(t);
    nodes.push({ template: t, instance: i });
    return nodes.length - 1;
  };
  return { templates, entityTemplates, instances, nodes, add };
};

/**
 * A decision graph from a legacy saved graph (the `.json` graph sessions and reports saved before decision graphs),
 * as the container's bytes, so older saves can be rewritten as `.dg`. Reading it back as the saved legacy graph
 * (decisionGraphToStoredGraph) gives the original. Instance indexes come from the paths: numeric segments become
 * positions, others (external ids) stay strings.
 */
export const graphRecordsToDecisionGraph = (
  nodeRecords: any[], edgeRecords: any[], graphOptions: any, graphValue: any,
  options: { session: boolean; ruleData?: boolean },
): Uint8Array => {
  const tables = pathTables();
  const { templates, entityTemplates, instances, nodes } = tables;
  const nodeIds = new Map<string, number>();
  const addNode = (v: string) => nodeIds.set(v, tables.add(v));

  const values: DecisionGraphParts["values"] = [];
  for (const node of nodeRecords) {
    addNode(node.v);
    const value = node.value ?? {};
    const own: DecisionGraphNodeValues = {};
    if (value.input !== undefined) own.input = value.input;
    if (value.derived !== undefined) own.derived = value.derived;
    if (value.justification !== undefined) own.justification = value.justification;
    if (value.triggered !== undefined) own.triggered = value.triggered;
    if (value.debugInfo?.reason !== undefined) own.reason = value.debugInfo.reason;
    values.push(Object.keys(own).length > 0 ? own : null);
  }
  const nodeCount = values.length;

  // saved edges were the solved edges; readers add the release's own edges from the rule graph
  const edges: [number, number, number][] = [];
  const seen = new Set<string>();
  for (const edge of edgeRecords ?? []) {
    const key = `${edge.v}\u0000${edge.w}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (!nodeIds.has(edge.v)) addNode(edge.v);
    if (!nodeIds.has(edge.w)) addNode(edge.w);
    edges.push([nodeIds.get(edge.v)!, nodeIds.get(edge.w)!, EDGE_FROM_STATE]);
  }

  return writeDecisionGraph({
    session: options.session,
    // saved graphs were the report graph, which always carried debug reasons
    debug: true,
    ...(graphOptions !== undefined ? { options: graphOptions } : {}),
    templates,
    templateEntity: Uint8Array.from(templates, (_, id) => (entityTemplates.has(id) ? 1 : 0)),
    instances,
    nodes,
    nodeCount,
    values,
    edges,
    ...(!options.ruleData ? {
      persistentProperties: graphValue?.persistentProperties,
      nodeProperties: nodeRecords.map((node) => Object.fromEntries(
        Object.entries(node.value ?? {}).filter(([name]) => !RESERVED_PROPERTIES.has(name)),
      )),
    } : {}),
    ...(options.ruleData
      ? {
          // listed nodes are the rule graph's nodes, in its order
          ruleNodes: nodeRecords.map((node) => node.value),
          ...(graphValue !== undefined ? { ruleGraphValue: graphValue } : {}),
          ruleNodeExtras: nodeRecords.map(({ v: _v, value: _value, ...extra }: any) =>
            Object.keys(extra).length > 0 ? extra : undefined,
          ),
          ruleEdges: (edgeRecords ?? []).map((edge: any) => [nodeIds.get(edge.v)!, nodeIds.get(edge.w)!] as [number, number]),
          ruleEdgeLabels: (edgeRecords ?? []).map(({ v: _v, w: _w, ...label }: any) =>
            Object.keys(label).length > 0 ? label : undefined,
          ),
        }
      : {}),
  });
};

/** Upgrade a legacy input at the read boundary. */
export const legacyGraphToDecisionGraph = (
  legacy: { nodes: any[]; edges: any[]; options?: any; value?: any },
  options: { session: boolean; ruleData?: boolean },
): Uint8Array => graphRecordsToDecisionGraph(legacy.nodes, legacy.edges, legacy.options, legacy.value, options);

/**
 * A release's compiled rule graph (graphlib JSON, as releases save it) as a decision graph that carries the rule
 * data too, so it stands in for the JSON: `DecisionGraph.from(bytes)` gives the same node objects as
 * `releaseDecisionGraph(release)` without the release.
 */
export const releaseGraphToDecisionGraph = (ruleGraph: { nodes: any[]; edges: any[]; options?: any }): Uint8Array =>
  legacyGraphToDecisionGraph(ruleGraph, { session: false, ruleData: true });

/**
 * A release's compiled rule graph read back as the rule graph JSON it was written from (DecisionGraph.toRuleGraph),
 * for code that still works on the JSON. Undefined when the graph carries no rule data.
 */
export const decisionGraphToRuleGraph = (input: DecisionGraphInput) => DecisionGraph.from(input).toRuleGraph();

/** Rewrite an earlier named-section draft into the compact directory, retaining every section's contents. */
export const canonicalDecisionGraph = (input: Uint8Array, sourceModifiedAt?: string): Uint8Array => {
  const graph = DecisionGraph.from(input);
  const bytes = graph.toBytes();
  const length = new DataView(bytes.buffer, bytes.byteOffset).getUint32(4, true);
  const header = unpackr.unpack(bytes.subarray(8, 8 + length));
  if (header.formatVersion !== DECISION_GRAPH_FORMAT_VERSION || header.sections.every(Array.isArray)) return input;
  const original = header.sections;
  const sections = readSections(original);
  const oldStart = Math.ceil((8 + length) / 8) * 8;
  let offset = 0;
  const chunks = sections.map((section, index) => {
    const raw = bytes.subarray(oldStart + section.offset, oldStart + section.offset + section.length);
    let compact = { bytes: raw, width: section.width };
    if (integerSection(section.name) && raw.length) {
      const Type = columnType(signedSection(section.name), section.width);
      const values = new Type(raw.buffer as ArrayBuffer, raw.byteOffset, raw.length / section.width);
      const wide = signedSection(section.name) ? Int32Array.from(values) : Uint32Array.from(values);
      compact = narrowSection(section.name, new Uint8Array(wide.buffer));
    }
    offset = Math.ceil(offset / 8) * 8;
    const next = { ...section, offset, length: compact.bytes.length, width: compact.width };
    offset += compact.bytes.length;
    const entry = sectionEntry(next);
    if (Array.isArray(original[index]) && typeof original[index][0] === "number") entry[0] = original[index][0];
    return { section: next, entry, bytes: compact.bytes };
  });
  const packed = new Uint8Array(packr.pack({ ...header, ...(sourceModifiedAt ? { sourceModifiedAt } : {}), sections: chunks.map(chunk => chunk.entry) }));
  const start = Math.ceil((8 + packed.length) / 8) * 8;
  const result = new Uint8Array(start + offset);
  result.set(bytes.subarray(0, 4));
  new DataView(result.buffer).setUint32(4, packed.length, true);
  result.set(packed, 8);
  for (const chunk of chunks) result.set(chunk.bytes, start + chunk.section.offset);
  DecisionGraph.from(result);
  return result;
};
